// src/pages/ShiftAdminHub.js
// 班別管理後台整合頁：子分頁 — 今日快覽 / 排班 / 任務指派 / 異常回報 / 員工帳號 / 表單 / 閒置代辦
// 原生 HTML/CSS 風格，與 InventoryAdmin 一致；使用 admin JWT 呼叫 /api/admin/shift/*

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { sanitizeShiftHtml } from '../lib/shiftSanitize';
import { ShiftTreeView } from './shift/ShiftTaskTree';
import ScheduleMonthAdmin from './ScheduleMonthAdmin';
import './shift/shift.css';

// ---------- 共用工具 ----------
function todayYmd() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

const WD_LABEL = ['日', '一', '二', '三', '四', '五', '六'];
const SHIFT_LABEL = { early: '早班', late: '晚班', night: '夜班', maintenance: '維護班', all: '全部班別' };
const SHIFT_COLOR = { early: '#0369a1', late: '#b45309', night: '#6d28d9', maintenance: '#1d4ed8', all: '#475569' };
const ROLE_LABEL = { staff: '員工', supervisor: '小主管', owner: '老闆 Nana' };

function adminToken() {
  try { return localStorage.getItem('admin_token') || localStorage.getItem('shift_token') || ''; } catch { return ''; }
}

async function api(url, opts = {}) {
  const res = await fetch(url, {
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${adminToken()}` },
    ...opts,
  });
  return res.json();
}

function fmtDateTime(v) {
  if (!v) return '—';
  try { return new Date(v).toLocaleString('zh-TW', { hour12: false }); } catch { return String(v); }
}

// ---------- 共用樣式 ----------
const S = {
  card: { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 16, marginBottom: 16, boxShadow: '0 1px 3px rgba(0,0,0,.05)' },
  h: { fontSize: 17, fontWeight: 800, color: '#1f2937', margin: '0 0 12px' },
  btn: (bg = '#7b4f2f') => ({ padding: '8px 14px', border: 'none', borderRadius: 8, background: bg, color: '#fff', fontWeight: 700, fontSize: 14, cursor: 'pointer' }),
  btnGhost: { padding: '6px 12px', border: '1px solid #d1d5db', borderRadius: 8, background: '#fff', color: '#374151', fontWeight: 600, fontSize: 13, cursor: 'pointer' },
  input: { padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' },
  th: { textAlign: 'left', padding: '8px 10px', fontSize: 13, color: '#6b7280', borderBottom: '2px solid #e5e7eb', whiteSpace: 'nowrap' },
  td: { padding: '8px 10px', fontSize: 14, color: '#1f2937', borderBottom: '1px solid #f1f5f9', verticalAlign: 'top' },
  tag: (bg, color) => ({ display: 'inline-block', padding: '2px 8px', borderRadius: 999, fontSize: 12, fontWeight: 700, background: bg, color }),
  label: { display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 4 },
};

function Tag({ children, bg = '#eef2ff', color = '#3730a3' }) {
  return <span style={S.tag(bg, color)}>{children}</span>;
}

function ShiftTag({ type }) {
  if (!type) return <span style={{ color: '#9ca3af' }}>—</span>;
  return <Tag bg="#f1f5f9" color={SHIFT_COLOR[type] || '#475569'}>{SHIFT_LABEL[type] || type}</Tag>;
}

function WeekdayPicker({ value, onChange }) {
  const v = value || [];
  const toggle = (d) => {
    const set = new Set(v);
    if (set.has(d)) set.delete(d); else set.add(d);
    onChange([...set].sort((a, b) => a - b));
  };
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 10px' }}>
      {WD_LABEL.map((label, d) => (
        <label key={d} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 14, cursor: 'pointer' }}>
          <input type="checkbox" checked={v.includes(d)} onChange={() => toggle(d)} />{label}
        </label>
      ))}
    </div>
  );
}

// ============================================================
// 分頁 1：今日快覽
// ============================================================
function DashboardTab() {
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [bizDate, setBizDate] = useState('');
  const [byShift, setByShift] = useState({});
  const [available, setAvailable] = useState([]);
  const [open, setOpen] = useState({ early: true });

  const SHIFT_L = { early: '早班', late: '晚班', night: '夜班', maintenance: '維護班' };
  const ORDER = ['early', 'late', 'night', 'maintenance'];

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const d = await api('/api/admin/shift/today-overview');
      if (d?.ok) {
        setBizDate(d.bizDate);
        setByShift(d.byShift || {});
        setAvailable(d.availableShifts || []);
      } else if (d?.error === 'shift_tables_missing') {
        throw new Error('班別資料表尚未建立，請執行 migrate-shift-v3.sql / migrate-shift-v4.sql');
      } else {
        throw new Error(d?.message || '載入失敗');
      }
    } catch (e) { setErr(e.message || '載入失敗'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const renderLeaves = (nodes, depth = 0) => {
    const out = [];
    const walk = (arr, d) => {
      for (const n of arr || []) {
        if (n.nodeType === 'check' || n.nodeType === 'text') {
          const who = n.record?.doneByName || n.record?.updatedByName || '';
          const filled = n.nodeType === 'text' ? (n.record?.textValue || '') : '';
          const vd = Number(n.validDays) || 1;
          out.push(
            <div key={n.id} style={{
              padding: '8px 10px', marginLeft: d * 12, marginBottom: 4,
              background: n.done ? '#ecfdf5' : '#fff',
              border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 13,
            }}>
              <div style={{ fontWeight: 700, color: n.done ? '#047857' : '#111827' }}>
                {n.nodeType === 'check' ? (n.done ? '✓ ' : '☐ ') : '✎ '}{n.title}
                {n.done ? (
                  <Tag bg="#d1fae5" color="#047857">已做{n.sourceBizDate ? ` ${n.sourceBizDate}` : ''}</Tag>
                ) : null}
                {vd > 1 && !n.done ? <Tag bg="#ffedd5" color="#c2410c">每{vd}日</Tag> : null}
              </div>
              {vd > 1 && n.done && n.validUntil ? (
                <div style={{ fontSize: 12, color: '#b45309', marginTop: 2 }}>有效至 {n.validUntil}</div>
              ) : null}
              {who ? <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>完成／更新：{who}</div> : null}
              {filled ? (
                <div style={{ fontSize: 13, color: '#374151', marginTop: 4, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{filled}</div>
              ) : null}
            </div>
          );
        }
        if (n.children?.length) walk(n.children, d + 1);
      }
    };
    walk(nodes, depth);
    return out;
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#888' }}>載入中…</div>;
  if (err) return <div style={{ ...S.card, color: '#b91c1c' }}>{err} <button style={{ ...S.btnGhost, marginLeft: 8 }} onClick={load}>重試</button></div>;

  return (
    <div>
      <div style={S.card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <h3 style={{ ...S.h, margin: 0 }}>今日任務快覽（{bizDate}）</h3>
          <div style={{ display: 'flex', gap: 8 }}>
            <button style={S.btnGhost} onClick={load}>重新整理</button>
          </div>
        </div>
        <div style={{ fontSize: 13, color: '#6b7280', marginTop: 4 }}>
          今日可用班別：{(available || []).map((s) => SHIFT_L[s] || s).join('、') || '—'}
          {' · '}跨日／篩選請用上方「追蹤管理」
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 12 }}>
        {ORDER.map((st) => {
          const block = byShift[st];
          if (!block) return null;
          const p = block.progress || { pct: 0, done: 0, total: 0 };
          const isOpen = !!open[st];
          const muted = available.length && !available.includes(st);
          return (
            <div key={st} style={{
              ...S.card, margin: 0, opacity: muted ? 0.7 : 1,
              borderTop: `4px solid ${st === 'early' ? '#d97706' : st === 'late' ? '#059669' : st === 'night' ? '#db2777' : '#2563eb'}`,
            }}>
              <button
                type="button"
                onClick={() => setOpen((o) => ({ ...o, [st]: !o[st] }))}
                style={{ width: '100%', border: 'none', background: 'transparent', textAlign: 'left', cursor: 'pointer', padding: 0 }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                  <strong style={{ fontSize: 16 }}>{SHIFT_L[st]} {isOpen ? '▾' : '▸'}</strong>
                  <span style={{ fontWeight: 800, color: p.pct === 100 ? '#059669' : '#2563eb' }}>{p.pct || 0}%</span>
                </div>
                <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                  {p.done}/{p.total} 完成
                  {block.specialProgress?.total ? ` · 額外 ${block.specialProgress.done}/${block.specialProgress.total}` : ''}
                  {block.personalExtraProgress?.total ? ` · 綁定員工 ${block.personalExtraProgress.done}/${block.personalExtraProgress.total}` : ''}
                  {muted ? ' · 今日通常不排此班' : ''}
                </div>
                <div style={{ height: 6, borderRadius: 99, background: '#e5e7eb', marginTop: 8, overflow: 'hidden' }}>
                  <div style={{ width: `${p.pct || 0}%`, height: '100%', background: p.pct === 100 ? '#10b981' : '#3b82f6' }} />
                </div>
              </button>
              {isOpen && (
                <div style={{ marginTop: 12, maxHeight: 420, overflow: 'auto' }}>
                  {(block.shiftExtra || []).length > 0 && (
                    <>
                      <div style={{ fontSize: 12, fontWeight: 700, color: '#b45309', marginBottom: 6 }}>班別額外</div>
                      {renderLeaves(block.shiftExtra)}
                    </>
                  )}
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', margin: '10px 0 6px' }}>班別待辦</div>
                  {renderLeaves(block.regular)}
                  {(block.personalExtra || []).length > 0 && (
                    <>
                      <div style={{ fontSize: 12, fontWeight: 700, color: '#7c3aed', margin: '10px 0 6px' }}>綁定員工額外</div>
                      {renderLeaves(block.personalExtra)}
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ============================================================
// 分頁 2：排班管理（含當日小主管）
// ============================================================
function ScheduleTab() {
  const [date, setDate] = useState(todayYmd);
  const [rows, setRows] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ staffPhone: '', shiftType: 'early', isSupervisor: false });

  const load = useCallback(async (d) => {
    setLoading(true);
    try {
      const [sc, st] = await Promise.all([api(`/api/admin/shift/schedule?date=${d}`), api('/api/admin/shift/staff')]);
      if (sc?.ok) setRows(sc.rows || []);
      if (st?.ok) setStaff((st.staff || []).filter(s => s.isActive));
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(date); }, [date, load]);

  const add = async () => {
    if (!form.staffPhone) { alert('請選擇員工'); return; }
    const r = await api('/api/admin/shift/schedule', { method: 'POST', body: JSON.stringify({ date, ...form }) });
    if (r?.ok) { setRows(r.rows || []); setForm({ staffPhone: '', shiftType: 'early', isSupervisor: false }); }
    else alert('儲存失敗');
  };
  const remove = async (id) => {
    if (!window.confirm('移除此排班？')) return;
    const r = await api(`/api/admin/shift/schedule/${id}`, { method: 'DELETE' });
    if (r?.ok) load(date);
  };
  const setSupervisor = async (row) => {
    const r = await api('/api/admin/shift/schedule', { method: 'POST', body: JSON.stringify({ date, staffPhone: row.staffPhone, shiftType: row.shiftType, isSupervisor: true }) });
    if (r?.ok) setRows(r.rows || []);
  };

  const supervisor = rows.find(r => r.isSupervisor);
  const scheduledPhones = new Set(rows.map(r => r.staffPhone));
  const availableStaff = staff.filter(s => !scheduledPhones.has(s.phone));

  return (
    <div>
      <div style={S.card}>
        <div style={{ marginBottom: 12, padding: '10px 12px', background: '#fff7ed', borderRadius: 8, fontSize: 13, color: '#9a3412' }}>
          日常排班請改用員工前台「今日班表」（主管／老闆複選）。此頁為次要後台工具。
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
          <h3 style={{ ...S.h, margin: 0 }}>排班管理（次要）</h3>
          <input type="date" value={date} onChange={e => setDate(e.target.value)} style={S.input} />
          {supervisor
            ? <Tag bg="#fef3c7" color="#92400e">當日小主管：{supervisor.staffName}</Tag>
            : <Tag bg="#fee2e2" color="#991b1b">尚未指定當日小主管</Tag>}
        </div>

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', padding: '12px', background: '#f8fafc', borderRadius: 8, marginBottom: 12 }}>
          <select value={form.staffPhone} onChange={e => setForm(f => ({ ...f, staffPhone: e.target.value }))} style={{ ...S.input, minWidth: 160 }}>
            <option value="">選擇員工…</option>
            {availableStaff.map(s => <option key={s.phone} value={s.phone}>{s.name}（{ROLE_LABEL[s.role] || s.role}）</option>)}
          </select>
          <select value={form.shiftType} onChange={e => setForm(f => ({ ...f, shiftType: e.target.value }))} style={S.input}>
            <option value="early">早班</option><option value="late">晚班</option><option value="night">夜班</option><option value="maintenance">維護班</option>
          </select>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 14 }}>
            <input type="checkbox" checked={form.isSupervisor} onChange={e => setForm(f => ({ ...f, isSupervisor: e.target.checked }))} />設為當日小主管
          </label>
          <button style={S.btn()} onClick={add}>＋ 加入排班</button>
        </div>

        {loading ? <div style={{ padding: 20, color: '#888' }}>載入中…</div> : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 500 }}>
              <thead><tr><th style={S.th}>員工</th><th style={S.th}>角色</th><th style={S.th}>班別</th><th style={S.th}>當日主管</th><th style={S.th}>操作</th></tr></thead>
              <tbody>
                {rows.length === 0 && <tr><td style={S.td} colSpan={5}>此日尚無排班</td></tr>}
                {rows.map(r => {
                  const s = staff.find(x => x.phone === r.staffPhone);
                  return (
                    <tr key={r.id}>
                      <td style={S.td}>{r.staffName}</td>
                      <td style={S.td}>{s ? (ROLE_LABEL[s.role] || s.role) : '—'}</td>
                      <td style={S.td}><ShiftTag type={r.shiftType} /></td>
                      <td style={S.td}>{r.isSupervisor ? <Tag bg="#fef3c7" color="#92400e">★ 小主管</Tag> : <button style={S.btnGhost} onClick={() => setSupervisor(r)}>設為主管</button>}</td>
                      <td style={S.td}><button style={{ ...S.btnGhost, color: '#b91c1c' }} onClick={() => remove(r.id)}>移除</button></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================
// 分頁 3：任務指派
// ============================================================
const emptyTask = { title: '', description: '', taskMode: 'recurring', scheduleDays: [0, 1, 2, 3, 4, 5, 6], targetDate: todayYmd(), shiftType: 'all', assignedTo: '' };

function TasksTab() {
  const [tasks, setTasks] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // {mode:'create'|'edit', data}
  const [overview, setOverview] = useState(null);
  const [showOverview, setShowOverview] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [t, st] = await Promise.all([api('/api/admin/shift/tasks'), api('/api/admin/shift/staff')]);
      if (t?.ok) setTasks(t.tasks || []);
      if (st?.ok) setStaff((st.staff || []).filter(s => s.isActive));
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const loadOverview = async () => {
    const r = await api(`/api/admin/shift/tasks/overview?date=${todayYmd()}`);
    if (r?.ok) { setOverview(r); setShowOverview(true); }
  };

  const save = async (data) => {
    const isEdit = modal?.mode === 'edit';
    const url = isEdit ? `/api/admin/shift/tasks/${data.id}` : '/api/admin/shift/tasks';
    const r = await api(url, { method: isEdit ? 'PATCH' : 'POST', body: JSON.stringify(data) });
    if (r?.ok) { setModal(null); if (r.tasks) setTasks(r.tasks); else load(); }
    else alert(r?.error === 'target_date_required' ? '額外任務需指定日期' : '儲存失敗');
  };
  const toggleActive = async (t) => {
    const r = await api(`/api/admin/shift/tasks/${t.id}`, { method: 'PATCH', body: JSON.stringify({ isActive: !t.isActive }) });
    if (r?.ok) { if (r.tasks) setTasks(r.tasks); else load(); }
  };
  const remove = async (t) => {
    if (!window.confirm('永久刪除此任務及所有完成紀錄？')) return;
    const r = await api(`/api/admin/shift/tasks/${t.id}`, { method: 'DELETE' });
    if (r?.ok) load();
  };

  const recurring = tasks.filter(t => t.taskMode === 'recurring');
  const oneoff = tasks.filter(t => t.taskMode === 'oneoff');

  const renderTable = (list, emptyText) => (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 640 }}>
        <thead><tr>
          <th style={S.th}>標題</th><th style={S.th}>班別</th><th style={S.th}>指定對象</th>
          <th style={S.th}>週別 / 日期</th><th style={S.th}>狀態</th><th style={S.th}>操作</th>
        </tr></thead>
        <tbody>
          {list.length === 0 && <tr><td style={S.td} colSpan={6}>{emptyText}</td></tr>}
          {list.map(t => (
            <tr key={t.id}>
              <td style={S.td}>
                <div style={{ fontWeight: 700 }}>{t.title}</div>
                {t.description && <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>{t.description}</div>}
              </td>
              <td style={S.td}><ShiftTag type={t.shiftType} /></td>
              <td style={S.td}>{t.assigneeName ? <Tag bg="#e0e7ff" color="#3730a3">{t.assigneeName}</Tag> : <span style={{ color: '#6b7280' }}>整班</span>}</td>
              <td style={S.td}>{t.taskMode === 'recurring' ? (t.scheduleDays || []).map(d => `週${WD_LABEL[d]}`).join('、') : (t.targetDate ? String(t.targetDate).slice(0, 10) : '—')}</td>
              <td style={S.td}>{t.isActive ? <Tag bg="#dcfce7" color="#166534">啟用</Tag> : <Tag bg="#f3f4f6" color="#6b7280">停用</Tag>}</td>
              <td style={S.td}>
                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                  <button style={S.btnGhost} onClick={() => setModal({ mode: 'edit', data: { ...emptyTask, ...t, scheduleDays: t.scheduleDays || [], targetDate: t.targetDate ? String(t.targetDate).slice(0, 10) : todayYmd(), assignedTo: t.assignedTo || '' } })}>編輯</button>
                  <button style={S.btnGhost} onClick={() => toggleActive(t)}>{t.isActive ? '停用' : '啟用'}</button>
                  <button style={{ ...S.btnGhost, color: '#b91c1c' }} onClick={() => remove(t)}>刪除</button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        <button style={S.btn()} onClick={() => setModal({ mode: 'create', data: { ...emptyTask } })}>＋ 新增任務</button>
        <button style={S.btnGhost} onClick={loadOverview}>今日全員進度</button>
      </div>

      {loading ? <div style={{ padding: 40, textAlign: 'center', color: '#888' }}>載入中…</div> : (
        <>
          <div style={S.card}>
            <h3 style={S.h}>常態任務（依週別循環）</h3>
            {renderTable(recurring, '尚無常態任務')}
          </div>
          <div style={S.card}>
            <h3 style={S.h}>額外任務（指定日期）</h3>
            {renderTable(oneoff, '尚無額外任務')}
          </div>
        </>
      )}

      {modal && <TaskModal mode={modal.mode} initial={modal.data} staff={staff} onClose={() => setModal(null)} onSave={save} />}
      {showOverview && overview && <OverviewModal data={overview} onClose={() => setShowOverview(false)} />}
    </div>
  );
}

function TaskModal({ mode, initial, staff, onClose, onSave }) {
  const [d, setD] = useState(initial);
  const set = (k, v) => setD(s => ({ ...s, [k]: v }));
  return (
    <Modal title={mode === 'edit' ? '編輯任務' : '新增任務'} onClose={onClose}>
      <div style={{ display: 'grid', gap: 12 }}>
        <div>
          <label style={S.label}>任務標題 *</label>
          <input style={{ ...S.input, width: '100%' }} value={d.title} onChange={e => set('title', e.target.value)} maxLength={200} />
        </div>
        <div>
          <label style={S.label}>說明</label>
          <textarea style={{ ...S.input, width: '100%', minHeight: 60 }} value={d.description} onChange={e => set('description', e.target.value)} />
        </div>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <div>
            <label style={S.label}>類型</label>
            <select style={S.input} value={d.taskMode} onChange={e => set('taskMode', e.target.value)}>
              <option value="recurring">常態（依週別）</option>
              <option value="oneoff">額外（指定日期）</option>
            </select>
          </div>
          <div>
            <label style={S.label}>關聯班別</label>
            <select style={S.input} value={d.shiftType} onChange={e => set('shiftType', e.target.value)}>
              <option value="all">全部班別</option><option value="early">早班</option><option value="late">晚班</option><option value="night">夜班</option><option value="maintenance">維護班</option>
            </select>
          </div>
          <div>
            <label style={S.label}>指定對象</label>
            <select style={S.input} value={d.assignedTo} onChange={e => set('assignedTo', e.target.value)}>
              <option value="">整班（不指定）</option>
              {staff.map(s => <option key={s.phone} value={s.phone}>{s.name}</option>)}
            </select>
          </div>
        </div>
        {d.taskMode === 'recurring' ? (
          <div>
            <label style={S.label}>出現星期</label>
            <WeekdayPicker value={d.scheduleDays} onChange={v => set('scheduleDays', v)} />
          </div>
        ) : (
          <div>
            <label style={S.label}>執行日期 *</label>
            <input type="date" style={S.input} value={d.targetDate} onChange={e => set('targetDate', e.target.value)} />
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
          <button style={S.btnGhost} onClick={onClose}>取消</button>
          <button style={S.btn()} onClick={() => onSave(d)}>儲存</button>
        </div>
      </div>
    </Modal>
  );
}

function OverviewModal({ data, onClose }) {
  const [filter, setFilter] = useState('all'); // all | pending | done
  const tasks = (data.tasks || []).filter(t => filter === 'all' || (filter === 'done' ? t.done : !t.done));
  return (
    <Modal title={`今日任務進度（${data.bizDate}）`} onClose={onClose} wide>
      <div style={{ display: 'flex', gap: 12, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        <Tag bg="#dbeafe" color="#1e40af">共 {data.total} 項</Tag>
        <Tag bg="#dcfce7" color="#166534">完成 {data.doneCount}</Tag>
        <Tag bg="#fee2e2" color="#991b1b">未完成 {data.pendingCount}</Tag>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 4 }}>
          {[['all', '全部'], ['pending', '未完成'], ['done', '已完成']].map(([k, l]) => (
            <button key={k} style={{ ...S.btnGhost, ...(filter === k ? { background: '#7b4f2f', color: '#fff', borderColor: '#7b4f2f' } : {}) }} onClick={() => setFilter(k)}>{l}</button>
          ))}
        </div>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 560 }}>
          <thead><tr>
            <th style={S.th}>任務</th><th style={S.th}>班別</th><th style={S.th}>應完成</th><th style={S.th}>狀態</th><th style={S.th}>完成者</th>
          </tr></thead>
          <tbody>
            {tasks.length === 0 && <tr><td style={S.td} colSpan={5}>無符合項目</td></tr>}
            {tasks.map(t => (
              <tr key={t.id}>
                <td style={S.td}>{t.title}</td>
                <td style={S.td}><ShiftTag type={t.shiftType} /></td>
                <td style={S.td}>{(t.responsibleNames || []).join('、') || <span style={{ color: '#9ca3af' }}>無人排班</span>}</td>
                <td style={S.td}>{t.done ? <Tag bg="#dcfce7" color="#166534">已完成</Tag> : <Tag bg="#fee2e2" color="#991b1b">未完成</Tag>}</td>
                <td style={S.td}>{t.done ? `${t.doneByName || ''}${t.doneAt ? ' · ' + fmtDateTime(t.doneAt) : ''}` : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Modal>
  );
}

const ANN_COLORS = ['#111827', '#b91c1c', '#c2410c', '#b45309', '#047857', '#1d4ed8', '#6d28d9', '#be185d'];

// ============================================================
// 分頁 4：異常回報中心
// ============================================================
const ANOMALY_STATUS = { pending: { label: '待處理', bg: '#fee2e2', color: '#991b1b' }, acknowledged: { label: '已受理', bg: '#fef3c7', color: '#92400e' }, resolved: { label: '已解決', bg: '#dcfce7', color: '#166534' } };

function AnomaliesTab() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [detail, setDetail] = useState(null);
  const [note, setNote] = useState('');
  const [toAnn, setToAnn] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api(`/api/admin/shift/anomalies${filter ? `?status=${filter}` : ''}`);
      if (r?.ok) setReports(r.reports || []);
    } finally { setLoading(false); }
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const updateStatus = async (id, status, resolvedNote) => {
    const r = await api(`/api/admin/shift/anomalies/${id}`, { method: 'PATCH', body: JSON.stringify({ status, resolvedNote }) });
    if (r?.ok) { setDetail(null); setNote(''); load(); }
  };

  const openConvert = (r) => {
    const imgs = Array.isArray(r.imageUrls) ? r.imageUrls : [];
    const parts = [];
    if (r.content) parts.push(String(r.content));
    if (r.resolvedNote) parts.push(`老闆回覆：${r.resolvedNote}`);
    setToAnn({
      source: r,
      title: r.title || '',
      content: parts.join('\n\n'),
      textColor: '#b91c1c',
      imageUrl: '',
      isActive: true,
      startDate: '',
      endDate: '',
      confirmStart: '',
      confirmEnd: '',
      sortOrder: 0,
    });
  };

  const saveAnnouncement = async (data) => {
    const payload = {
      title: data.title,
      content: data.content || '',
      textColor: data.textColor || null,
      imageUrl: data.imageUrl || null,
      isActive: data.isActive !== false,
      startDate: data.startDate || null,
      endDate: data.endDate || null,
      confirmStart: data.confirmStart || null,
      confirmEnd: data.confirmEnd || null,
      sortOrder: Number(data.sortOrder) || 0,
    };
    const r = await api('/api/admin/shift/announcements', { method: 'POST', body: JSON.stringify(payload) });
    if (r?.ok) {
      setToAnn(null);
      alert('已轉成公告');
    } else alert('儲存公告失敗');
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 4, marginBottom: 16, flexWrap: 'wrap' }}>
        {[['', '全部'], ['pending', '待處理'], ['acknowledged', '已受理'], ['resolved', '已解決']].map(([k, l]) => (
          <button key={k} style={{ ...S.btnGhost, ...(filter === k ? { background: '#7b4f2f', color: '#fff', borderColor: '#7b4f2f' } : {}) }} onClick={() => setFilter(k)}>{l}</button>
        ))}
      </div>
      <div style={S.card}>
        {loading ? <div style={{ padding: 20, color: '#888' }}>載入中…</div> : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 640 }}>
              <thead><tr>
                <th style={S.th}>日期</th><th style={S.th}>回報者</th><th style={S.th}>班別</th>
                <th style={S.th}>摘要</th><th style={S.th}>狀態</th><th style={S.th}>操作</th>
              </tr></thead>
              <tbody>
                {reports.length === 0 && <tr><td style={S.td} colSpan={6}>無回報紀錄</td></tr>}
                {reports.map(r => {
                  const st = ANOMALY_STATUS[r.status] || ANOMALY_STATUS.pending;
                  return (
                    <tr key={r.id}>
                      <td style={S.td}>{String(r.bizDate).slice(0, 10)}</td>
                      <td style={S.td}>{r.reporterName}</td>
                      <td style={S.td}><ShiftTag type={r.shiftType} /></td>
                      <td style={S.td}>{r.title}</td>
                      <td style={S.td}><Tag bg={st.bg} color={st.color}>{st.label}</Tag></td>
                      <td style={S.td}>
                        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                          <button style={S.btnGhost} onClick={() => { setDetail(r); setNote(r.resolvedNote || ''); }}>查看 / 處理</button>
                          <button style={S.btnGhost} onClick={() => openConvert(r)}>轉成公告</button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {detail && (
        <Modal title="異常回報詳情" onClose={() => setDetail(null)}>
          <div style={{ display: 'grid', gap: 10 }}>
            <div><b>{detail.title}</b></div>
            <div style={{ fontSize: 13, color: '#6b7280' }}>{detail.reporterName} · {String(detail.bizDate).slice(0, 10)} · {SHIFT_LABEL[detail.shiftType] || '—'} · {fmtDateTime(detail.createdAt)}</div>
            <div style={{ whiteSpace: 'pre-wrap', background: '#f8fafc', padding: 12, borderRadius: 8, fontSize: 14 }}>{detail.content || '（無詳細內容）'}</div>
            {Array.isArray(detail.imageUrls) && detail.imageUrls.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {detail.imageUrls.map((url) => (
                  <a key={url} href={url} target="_blank" rel="noopener noreferrer">
                    <img src={url} alt="" style={{ width: 96, height: 96, objectFit: 'cover', borderRadius: 8, border: '1px solid #e5e7eb' }} />
                  </a>
                ))}
              </div>
            )}
            <div>
              <label style={S.label}>處理備註</label>
              <textarea style={{ ...S.input, width: '100%', minHeight: 60 }} value={note} onChange={e => setNote(e.target.value)} />
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              <button style={S.btnGhost} onClick={() => openConvert(detail)}>轉成公告</button>
              <button style={S.btnGhost} onClick={() => updateStatus(detail.id, 'acknowledged', note)}>標記已受理</button>
              <button style={S.btn('#166534')} onClick={() => updateStatus(detail.id, 'resolved', note)}>標記已解決</button>
            </div>
          </div>
        </Modal>
      )}

      {toAnn && (
        <AnomalyToAnnouncementModal
          data={toAnn}
          onClose={() => setToAnn(null)}
          onChange={setToAnn}
          onSave={saveAnnouncement}
        />
      )}
    </div>
  );
}

function AnomalyToAnnouncementModal({ data, onClose, onChange, onSave }) {
  const src = data.source || {};
  const imgs = Array.isArray(src.imageUrls) ? src.imageUrls : [];
  const st = ANOMALY_STATUS[src.status] || ANOMALY_STATUS.pending;
  const set = (k, v) => onChange((s) => ({ ...s, [k]: v }));
  const pickImg = (url) => set('imageUrl', data.imageUrl === url ? '' : url);

  return (
    <Modal title="轉成公告" onClose={onClose} maxWidth={920}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, alignItems: 'start' }}>
        <div style={{ display: 'grid', gap: 10, background: '#f8fafc', borderRadius: 10, padding: 14, border: '1px solid #e5e7eb' }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#6b7280' }}>回報內容</div>
          <div style={{ fontWeight: 800, fontSize: 16 }}>{src.title}</div>
          <div style={{ fontSize: 12, color: '#6b7280' }}>
            {src.reporterName} · {String(src.bizDate || '').slice(0, 10)} · {SHIFT_LABEL[src.shiftType] || '—'}
            {' · '}<Tag bg={st.bg} color={st.color}>{st.label}</Tag>
          </div>
          <div style={{ whiteSpace: 'pre-wrap', fontSize: 14, lineHeight: 1.5 }}>{src.content || '（無詳細內容）'}</div>
          {src.resolvedNote ? (
            <div style={{ fontSize: 13, background: '#fff', borderRadius: 8, padding: 10, border: '1px solid #e5e7eb' }}>
              老闆回覆：{src.resolvedNote}
            </div>
          ) : null}
          {imgs.length > 0 && (
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', marginBottom: 6 }}>點選沿用為公告圖（單選）</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {imgs.map((url) => (
                  <button
                    key={url}
                    type="button"
                    onClick={() => pickImg(url)}
                    style={{
                      padding: 0, borderRadius: 8, cursor: 'pointer', overflow: 'hidden',
                      border: data.imageUrl === url ? '3px solid #7b4f2f' : '1px solid #e5e7eb',
                      boxShadow: data.imageUrl === url ? '0 0 0 2px rgba(123,79,47,.25)' : 'none',
                    }}
                  >
                    <img src={url} alt="" style={{ width: 88, height: 88, objectFit: 'cover', display: 'block' }} />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <div style={{ display: 'grid', gap: 12 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#6b7280' }}>公告內容</div>
          <div>
            <label style={S.label}>標題 *</label>
            <input style={{ ...S.input, width: '100%', color: data.textColor || '#111827', fontWeight: 800 }} value={data.title || ''} onChange={(e) => set('title', e.target.value)} />
          </div>
          <div>
            <label style={S.label}>標題顏色</label>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {ANN_COLORS.map((c) => (
                <button key={c} type="button" onClick={() => set('textColor', c)}
                  style={{ width: 26, height: 26, borderRadius: 6, cursor: 'pointer', background: c, border: data.textColor === c ? '3px solid #111' : '1px solid #d1d5db' }} />
              ))}
            </div>
          </div>
          <div>
            <label style={S.label}>內容</label>
            <textarea style={{ ...S.input, width: '100%', minHeight: 120 }} value={data.content || ''} onChange={(e) => set('content', e.target.value)} />
          </div>
          {data.imageUrl ? (
            <div>
              <label style={S.label}>已選公告圖</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <img src={data.imageUrl} alt="" style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 8, border: '1px solid #e5e7eb' }} />
                <button type="button" style={S.btnGhost} onClick={() => set('imageUrl', '')}>清除</button>
              </div>
            </div>
          ) : null}
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <div>
              <label style={S.label}>顯示起始</label>
              <input type="date" style={S.input} value={data.startDate || ''} onChange={(e) => set('startDate', e.target.value)} />
            </div>
            <div>
              <label style={S.label}>顯示截止</label>
              <input type="date" style={S.input} value={data.endDate || ''} onChange={(e) => set('endDate', e.target.value)} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <div>
              <label style={S.label}>每日確認起</label>
              <input type="date" style={S.input} value={data.confirmStart || ''} onChange={(e) => set('confirmStart', e.target.value)} />
            </div>
            <div>
              <label style={S.label}>每日確認迄</label>
              <input type="date" style={S.input} value={data.confirmEnd || ''} onChange={(e) => set('confirmEnd', e.target.value)} />
            </div>
            <div>
              <label style={S.label}>排序</label>
              <input type="number" style={{ ...S.input, width: 100 }} value={data.sortOrder ?? 0} onChange={(e) => set('sortOrder', e.target.value)} />
            </div>
          </div>
          <label style={{ display: 'inline-flex', gap: 6, alignItems: 'center', fontSize: 14 }}>
            <input type="checkbox" checked={data.isActive !== false} onChange={(e) => set('isActive', e.target.checked)} />
            立即啟用
          </label>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button style={S.btnGhost} onClick={onClose}>取消</button>
            <button style={S.btn()} onClick={() => { if (!(data.title || '').trim()) { alert('請填標題'); return; } onSave(data); }}>發佈公告</button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

// ============================================================
// 分頁 5：員工管理（帳號 + 排班屬性合併）
// ============================================================
const STAFF_TAG_LABEL = { parttime: '櫃台兼職', fulltime: '櫃台正職', support: '支援櫃台正職', other: '其餘正職', venue: '包場' };
const FILL_MODE_LABEL = { can: '填可上', cannot: '填不能上' };

function StaffTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [pwModal, setPwModal] = useState(null);
  const [metaModal, setMetaModal] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api('/api/admin/shift/staff'); if (r?.ok) setRows(r.staff || []); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (data) => {
    const isEdit = modal?.mode === 'edit';
    let r;
    const code = String(data.staffCode || '').trim();
    if (isEdit) r = await api(`/api/admin/shift/staff/${data.phone}`, { method: 'PATCH', body: JSON.stringify({ name: data.name, role: data.role, staffCode: code }) });
    else r = await api('/api/admin/shift/staff', { method: 'POST', body: JSON.stringify({ phone: data.phone.trim(), name: data.name.trim(), role: data.role, password: data.password || undefined, code }) });
    if (r?.ok) { setModal(null); load(); }
    else alert(r?.error === 'phone_exists' ? '此電話已存在' : (r?.error === 'code_exists' ? '此員工代號已被使用' : (r?.error === 'bad_code' ? '員工代號須為 4–6 碼數字' : (r?.error === 'missing_fields' ? '請填寫姓名與員工代號' : '儲存失敗'))));
  };
  const saveMeta = async (phone, data) => {
    const r = await api(`/api/admin/shift/staff/${phone}/meta`, { method: 'PATCH', body: JSON.stringify(data) });
    if (r?.ok) { setMetaModal(null); load(); } else alert('儲存排班屬性失敗');
  };
  const resetPw = async (phone, password) => {
    const r = await api(`/api/admin/shift/staff/${phone}/reset-password`, { method: 'POST', body: JSON.stringify({ password }) });
    if (r?.ok) { setPwModal(null); alert('密碼已重設'); } else alert('重設失敗');
  };
  const toggleActive = async (r) => {
    if (r.isActive) { const res = await api(`/api/admin/shift/staff/${r.phone}`, { method: 'DELETE' }); if (res?.ok) load(); }
    else { const res = await api(`/api/admin/shift/staff/${r.phone}`, { method: 'PATCH', body: JSON.stringify({ isActive: true }) }); if (res?.ok) load(); }
  };

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <button style={S.btn()} onClick={() => setModal({ mode: 'create', data: { phone: '', name: '', role: 'staff', password: '', staffCode: '' } })}>＋ 新增員工</button>
      </div>
      <div style={S.card}>
        {loading ? <div style={{ padding: 20, color: '#888' }}>載入中…</div> : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 700 }}>
              <thead><tr>
                <th style={S.th}>代號</th><th style={S.th}>姓名</th><th style={S.th}>角色</th>
                <th style={S.th}>身分TAG</th><th style={S.th}>填寫模式</th>
                <th style={S.th}>狀態</th><th style={S.th}>操作</th>
              </tr></thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r.phone} style={!r.staffCode ? { background: '#fff7ed' } : undefined}>
                    <td style={S.td}>{r.staffCode ? <Tag bg="#eef2ff" color="#3730a3">{r.staffCode}</Tag> : <Tag bg="#fee2e2" color="#991b1b">缺代號</Tag>}</td>
                    <td style={S.td}>{r.name}</td>
                    <td style={S.td}>{ROLE_LABEL[r.role] || r.role}</td>
                    <td style={S.td}>{r.meta ? (STAFF_TAG_LABEL[r.meta.identityGroup] || r.meta.identityGroup || '—') : <span style={{ color: '#9ca3af' }}>未綁定</span>}</td>
                    <td style={S.td}>{r.meta ? (FILL_MODE_LABEL[r.meta.fillMode] || r.meta.fillMode || '—') : '—'}</td>
                    <td style={S.td}>{r.isActive ? <Tag bg="#dcfce7" color="#166534">啟用</Tag> : <Tag bg="#f3f4f6" color="#6b7280">停用</Tag>}</td>
                    <td style={S.td}>
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        <button style={S.btnGhost} onClick={() => setModal({ mode: 'edit', data: { ...r } })}>帳號</button>
                        <button style={S.btnGhost} onClick={() => setMetaModal({ phone: r.phone, name: r.name, meta: r.meta || { displayName: r.name, identityGroup: 'parttime', fillMode: 'can', sortOrder: 99, isActive: true } })}>排班</button>
                        <button style={S.btnGhost} onClick={() => setPwModal({ phone: r.phone, password: '' })}>密碼</button>
                        <button style={{ ...S.btnGhost, color: r.isActive ? '#b91c1c' : '#166534' }} onClick={() => toggleActive(r)}>{r.isActive ? '停用' : '啟用'}</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modal && <StaffModal mode={modal.mode} initial={modal.data} onClose={() => setModal(null)} onSave={save} />}
      {metaModal && <StaffMetaEditModal phone={metaModal.phone} name={metaModal.name} initial={metaModal.meta} onClose={() => setMetaModal(null)} onSave={(data) => saveMeta(metaModal.phone, data)} />}
      {pwModal && (
        <Modal title="重設密碼" onClose={() => setPwModal(null)}>
          <div style={{ display: 'grid', gap: 12 }}>
            <div>
              <label style={S.label}>新密碼（至少 4 碼）</label>
              <input type="password" style={{ ...S.input, width: '100%' }} value={pwModal.password} onChange={e => setPwModal(p => ({ ...p, password: e.target.value }))} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button style={S.btnGhost} onClick={() => setPwModal(null)}>取消</button>
              <button style={S.btn()} onClick={() => { if ((pwModal.password || '').length < 4) { alert('至少 4 碼'); return; } resetPw(pwModal.phone, pwModal.password); }}>確認</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

function StaffModal({ mode, initial, onClose, onSave }) {
  const [d, setD] = useState(initial);
  const set = (k, v) => setD(s => ({ ...s, [k]: v }));
  const isEdit = mode === 'edit';
  return (
    <Modal title={isEdit ? '編輯帳號' : '新增帳號'} onClose={onClose}>
      <div style={{ display: 'grid', gap: 12 }}>
        <div>
          <label style={S.label}>員工代號（4–6 碼，登入用）*</label>
          <input style={{ ...S.input, width: '100%' }} value={d.staffCode || ''} inputMode="numeric" maxLength={6}
            onChange={e => set('staffCode', e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="例如 1234" />
        </div>
        <div>
          <label style={S.label}>姓名 *</label>
          <input style={{ ...S.input, width: '100%' }} value={d.name} maxLength={50} onChange={e => set('name', e.target.value)} />
        </div>
        <div>
          <label style={S.label}>電話（選填，系統內部識別）</label>
          <input style={{ ...S.input, width: '100%' }} value={d.phone} disabled={isEdit} onChange={e => set('phone', e.target.value)} placeholder="空白則自動佔位" />
        </div>
        <div>
          <label style={S.label}>角色</label>
          <select style={{ ...S.input, width: '100%' }} value={d.role} onChange={e => set('role', e.target.value)}>
            <option value="staff">員工</option><option value="supervisor">小主管</option><option value="owner">老闆 Nana</option>
          </select>
        </div>
        {!isEdit && (
          <div>
            <label style={S.label}>相容密碼（選填，已改代號登入）</label>
            <input type="password" style={{ ...S.input, width: '100%' }} value={d.password} onChange={e => set('password', e.target.value)} />
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button style={S.btnGhost} onClick={onClose}>取消</button>
          <button style={S.btn()} onClick={() => {
            if (!d.name) { alert('請填寫姓名'); return; }
            if (!/^\d{4,6}$/.test(String(d.staffCode || '').trim())) { alert('員工代號須為 4–6 碼數字'); return; }
            onSave(d);
          }}>儲存</button>
        </div>
      </div>
    </Modal>
  );
}

function StaffMetaEditModal({ name, initial, onClose, onSave }) {
  const [d, setD] = useState({ ...initial });
  const set = (k, v) => setD(s => ({ ...s, [k]: v }));
  const fillMode = d.fillMode === 'cannot' ? 'cannot' : 'can';
  return (
    <Modal title={`排班屬性 · ${name}`} onClose={onClose}>
      <div style={{ display: 'grid', gap: 12 }}>
        <div>
          <label style={S.label}>顯示名（班表上的名稱）</label>
          <input style={{ ...S.input, width: '100%' }} value={d.displayName || ''} maxLength={50} onChange={e => set('displayName', e.target.value)} />
        </div>
        <div>
          <label style={S.label}>身分 TAG</label>
          <select style={{ ...S.input, width: '100%' }} value={d.identityGroup || ''} onChange={e => set('identityGroup', e.target.value)}>
            {Object.entries(STAFF_TAG_LABEL).map(([k, lab]) => <option key={k} value={k}>{lab}</option>)}
          </select>
        </div>
        <div>
          <label style={S.label}>填寫模式</label>
          <select style={{ ...S.input, width: '100%' }} value={fillMode} onChange={e => set('fillMode', e.target.value)}>
            <option value="can">兼職（填可上）</option>
            <option value="cannot">正職（填不能上）</option>
          </select>
        </div>
        <div>
          <label style={S.label}>排序</label>
          <input style={{ ...S.input, width: '100%' }} type="number" min="0" value={d.sortOrder ?? 99} onChange={e => set('sortOrder', Number(e.target.value) || 0)} />
        </div>
        <label>
          <input type="checkbox" checked={d.isActive !== false} onChange={e => set('isActive', e.target.checked)} /> 列入排班
        </label>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button style={S.btnGhost} onClick={onClose}>取消</button>
          <button style={S.btn()} onClick={() => onSave({ ...d, fillMode })}>儲存</button>
        </div>
      </div>
    </Modal>
  );
}

// ============================================================
// 分頁 6：表單管理
// ============================================================
function FormsTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api('/api/admin/shift/forms'); if (r?.ok) setRows(r.forms || []); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggleActive = async (r) => {
    const res = await api(`/api/admin/shift/forms/${r.id}`, { method: 'PATCH', body: JSON.stringify({ isActive: !r.isActive }) });
    if (res?.ok) load();
  };
  const remove = async (r) => {
    if (!window.confirm('永久刪除此表單及所有紀錄？無法復原')) return;
    const res = await api(`/api/admin/shift/forms/${r.id}`, { method: 'DELETE' });
    if (res?.ok) load();
  };

  return (
    <div>
      <div style={S.card}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
          <h3 style={{ ...S.h, margin: 0 }}>表單定義管理</h3>
          <span style={{ fontSize: 13, color: '#6b7280' }}>表單內容（段落／項目）請於前台主管介面編輯</span>
        </div>
        {loading ? <div style={{ padding: 20, color: '#888' }}>載入中…</div> : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 720 }}>
              <thead><tr>
                <th style={S.th}>表單 ID</th><th style={S.th}>名稱</th><th style={S.th}>類型</th>
                <th style={S.th}>出現星期</th><th style={S.th}>時段</th><th style={S.th}>段落/項目</th><th style={S.th}>狀態</th><th style={S.th}>操作</th>
              </tr></thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r.id}>
                    <td style={S.td}>{r.id}</td>
                    <td style={S.td}>{r.name}</td>
                    <td style={S.td}>{r.meta ? <Tag>{r.meta}</Tag> : '—'}</td>
                    <td style={S.td}>{(r.scheduleDays || []).map(d => `週${WD_LABEL[d]}`).join('、') || '—'}</td>
                    <td style={S.td}>{r.startHour}:00–{r.endHour}:00</td>
                    <td style={S.td}>{r.sectionCount} / {r.itemCount}</td>
                    <td style={S.td}>{r.isActive ? <Tag bg="#dcfce7" color="#166534">啟用</Tag> : <Tag bg="#f3f4f6" color="#6b7280">停用</Tag>}</td>
                    <td style={S.td}>
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        <button style={{ ...S.btnGhost, color: r.isActive ? '#b91c1c' : '#166534' }} onClick={() => toggleActive(r)}>{r.isActive ? '停用' : '啟用'}</button>
                        <button style={{ ...S.btnGhost, color: '#b91c1c' }} onClick={() => remove(r)}>刪除</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================
// 分頁 7：閒置代辦
// ============================================================
function IdleTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api('/api/admin/shift/idle-tasks'); if (r?.ok) setRows(r.tasks || []); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (data) => {
    const isEdit = !!data.id;
    const url = isEdit ? `/api/admin/shift/idle-tasks/${data.id}` : '/api/admin/shift/idle-tasks';
    const r = await api(url, { method: isEdit ? 'PATCH' : 'POST', body: JSON.stringify({ name: data.name.trim(), taskType: data.taskType }) });
    if (r?.ok) { setModal(null); load(); } else alert('儲存失敗');
  };
  const toggleActive = async (r) => { const res = await api(`/api/admin/shift/idle-tasks/${r.id}`, { method: 'PATCH', body: JSON.stringify({ isActive: !r.isActive }) }); if (res?.ok) load(); };
  const resetCd = async (r) => { const res = await api(`/api/admin/shift/idle-tasks/${r.id}/reset-cooldown`, { method: 'POST' }); if (res?.ok) load(); };
  const remove = async (r) => { if (!window.confirm('刪除此項目？')) return; const res = await api(`/api/admin/shift/idle-tasks/${r.id}`, { method: 'DELETE' }); if (res?.ok) load(); };

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <button style={S.btn()} onClick={() => setModal({ name: '', taskType: 'supply' })}>＋ 新增項目</button>
      </div>
      <div style={S.card}>
        {loading ? <div style={{ padding: 20, color: '#888' }}>載入中…</div> : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 640 }}>
              <thead><tr><th style={S.th}>名稱</th><th style={S.th}>類型</th><th style={S.th}>上次完成</th><th style={S.th}>冷卻至</th><th style={S.th}>狀態</th><th style={S.th}>操作</th></tr></thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r.id}>
                    <td style={S.td}>{r.name}</td>
                    <td style={S.td}>{r.taskType === 'clean' ? <Tag bg="#dcfce7" color="#166534">清潔</Tag> : <Tag bg="#dbeafe" color="#1e40af">備料</Tag>}</td>
                    <td style={S.td}>{fmtDateTime(r.lastDoneAt)}</td>
                    <td style={S.td}>{r.cooldownUntil ? fmtDateTime(r.cooldownUntil) : '—'}</td>
                    <td style={S.td}>{r.isActive ? <Tag bg="#dcfce7" color="#166534">啟用</Tag> : <Tag bg="#f3f4f6" color="#6b7280">停用</Tag>}</td>
                    <td style={S.td}>
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        <button style={S.btnGhost} onClick={() => setModal({ ...r })}>編輯</button>
                        {r.taskType === 'clean' && <button style={S.btnGhost} onClick={() => resetCd(r)}>重置冷卻</button>}
                        <button style={{ ...S.btnGhost, color: r.isActive ? '#b91c1c' : '#166534' }} onClick={() => toggleActive(r)}>{r.isActive ? '停用' : '啟用'}</button>
                        <button style={{ ...S.btnGhost, color: '#b91c1c' }} onClick={() => remove(r)}>刪除</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modal && (
        <Modal title={modal.id ? '編輯項目' : '新增項目'} onClose={() => setModal(null)}>
          <div style={{ display: 'grid', gap: 12 }}>
            <div>
              <label style={S.label}>名稱 *</label>
              <input style={{ ...S.input, width: '100%' }} value={modal.name} maxLength={200} onChange={e => setModal(m => ({ ...m, name: e.target.value }))} />
            </div>
            <div>
              <label style={S.label}>類型</label>
              <select style={{ ...S.input, width: '100%' }} value={modal.taskType} onChange={e => setModal(m => ({ ...m, taskType: e.target.value }))}>
                <option value="supply">備料（每日重置）</option>
                <option value="clean">清潔（完成後 7 天冷卻）</option>
              </select>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button style={S.btnGhost} onClick={() => setModal(null)}>取消</button>
              <button style={S.btn()} onClick={() => { if (!modal.name.trim()) { alert('請輸入名稱'); return; } save(modal); }}>儲存</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ---------- 共用 Modal ----------
function Modal({ title, onClose, children, wide, maxWidth }) {
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.4)', zIndex: 9000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '5vh 16px', overflow: 'auto' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 14, padding: 20, width: '100%', maxWidth: maxWidth || (wide ? 760 : 480), boxShadow: '0 20px 60px rgba(0,0,0,.25)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800 }}>{title}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#9ca3af', lineHeight: 1 }}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

// ============================================================
// 任務樹管理（TaskNode）+ 右側預覽
// ============================================================
const NODE_TYPE_LABEL = { category: '分類', check: '核選', text: '填空' };
const SHIFT_OPTS = [
  { id: 'early', label: '早' },
  { id: 'late', label: '晚' },
  { id: 'night', label: '夜' },
  { id: 'maintenance', label: '維護' },
];

function newAssignId() {
  return `a_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

/** 從節點取得指派列（含舊欄位展開） */
function getNodeAssignments(n) {
  const dates = [];
  if (Array.isArray(n?.assignments) && n.assignments.length) {
    for (const a of n.assignments) {
      const date = String(a.date || '').slice(0, 10);
      if (date) dates.push(date);
    }
  } else {
    const from = (n?.targetDates || []).length
      ? n.targetDates.map((d) => String(d).slice(0, 10))
      : (n?.targetDate ? [String(n.targetDate).slice(0, 10)] : []);
    dates.push(...from);
  }
  return [...new Set(dates.filter(Boolean))].map((date) => ({
    id: `d_${date}`,
    date,
    kind: 'day',
  }));
}

// 類型視覺：分類（藍底白字）／核選（白底綠字＋框框）／填空（白底橘字）
const NODE_TYPE_STYLE = {
  category: { bg: '#3b82f6', color: '#fff', border: '1px solid #3b82f6', icon: '▸' },
  check: { bg: '#fff', color: '#16a34a', border: '1.5px solid #16a34a', icon: '☐' },
  text: { bg: '#fff', color: '#ea580c', border: '1.5px solid #ea580c', icon: '✏️' },
};

function TypeTag({ type }) {
  const c = NODE_TYPE_STYLE[type] || { bg: '#f3f4f6', color: '#6b7280', border: '1px solid #e5e7eb', icon: '•' };
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 9px',
      borderRadius: 6, background: c.bg, color: c.color, border: c.border,
      fontSize: 12, fontWeight: 800, whiteSpace: 'nowrap',
    }}>
      <span>{c.icon}</span>
      <span>{NODE_TYPE_LABEL[type] || type}</span>
    </span>
  );
}

function WeekdayDisplay({ days }) {
  const arr = Array.isArray(days) ? days : [];
  if (arr.length === 0) return <span style={{ color: '#9ca3af', fontSize: 12 }}>未設定</span>;
  if (arr.length === 7) return <span style={{ color: '#059669', fontSize: 12, fontWeight: 700 }}>每天</span>;
  return (
    <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
      {[...arr].sort((a, b) => a - b).map((d) => (
        <span key={d} style={{ padding: '1px 6px', background: '#e0e7ff', color: '#4f46e5', borderRadius: 4, fontSize: 11, fontWeight: 700 }}>
          {WD_LABEL[d]}
        </span>
      ))}
    </div>
  );
}

const DEFAULT_FILTER = { shiftType: '', weekdays: [], nodeType: '', specialOnly: false, keyword: '', extraKind: '' };

function pad2(n) { return String(n).padStart(2, '0'); }
function toYmd(d) {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}
/** 當月第一個周日 YYYY-MM-DD */
function firstSundayOfMonth(ref = new Date()) {
  const d = new Date(ref.getFullYear(), ref.getMonth(), 1);
  while (d.getDay() !== 0) d.setDate(d.getDate() + 1);
  return toYmd(d);
}
/** 當月最後一個周日 YYYY-MM-DD */
function lastSundayOfMonth(ref = new Date()) {
  const d = new Date(ref.getFullYear(), ref.getMonth() + 1, 0);
  while (d.getDay() !== 0) d.setDate(d.getDate() - 1);
  return toYmd(d);
}

function WeekShiftMatrix({ onSaved }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const r = await api('/api/admin/shift/week-shifts');
      if (r?.ok) setRows(r.rows || []);
      else setErr(r?.message || '載入失敗（請確認已執行 migrate-shift-v5.sql）');
    } catch (e) { setErr(e.message || '載入失敗'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const enabled = (wd, st) => {
    const hit = rows.find((x) => Number(x.weekday) === wd && x.shiftType === st);
    return hit ? !!hit.isEnabled : false;
  };
  const toggle = (wd, st) => {
    setRows((prev) => {
      const next = [...prev];
      const i = next.findIndex((x) => Number(x.weekday) === wd && x.shiftType === st);
      if (i >= 0) next[i] = { ...next[i], isEnabled: !next[i].isEnabled };
      else next.push({ weekday: wd, shiftType: st, isEnabled: true });
      return next;
    });
  };
  const save = async () => {
    setSaving(true);
    try {
      const r = await api('/api/admin/shift/week-shifts', { method: 'PUT', body: JSON.stringify({ rows }) });
      if (r?.ok) {
        setRows(r.rows || rows);
        onSaved?.(r.rows || rows);
      }
      else alert(r?.message || '儲存失敗');
    } catch { alert('儲存失敗'); }
    finally { setSaving(false); }
  };

  if (loading) return <div style={{ ...S.card, color: '#888' }}>載入星期×班別…</div>;
  if (err) return <div style={{ ...S.card, color: '#b91c1c' }}>{err} <button style={S.btnGhost} onClick={load}>重試</button></div>;

  return (
    <div style={{ ...S.card, borderTop: '4px solid #0369a1', marginBottom: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          style={{ ...S.h, margin: 0, border: 'none', background: 'none', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', gap: 8 }}
        >
          <span>{open ? '▾' : '▸'}</span>
          星期 × 班別（未勾選＝當日無此班）
        </button>
        {open && (
          <button style={S.btn('#0369a1')} disabled={saving} onClick={save}>{saving ? '儲存中…' : '儲存班別設定'}</button>
        )}
      </div>
      {open && (
        <div style={{ overflowX: 'auto', marginTop: 10 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 520 }}>
            <thead>
              <tr>
                <th style={S.th}>班別</th>
                {WD_LABEL.map((w, i) => <th key={i} style={{ ...S.th, textAlign: 'center' }}>{w}</th>)}
              </tr>
            </thead>
            <tbody>
              {SHIFT_OPTS.map((s) => (
                <tr key={s.id}>
                  <td style={S.td}><strong style={{ color: SHIFT_COLOR[s.id] }}>{s.label}</strong></td>
                  {[0, 1, 2, 3, 4, 5, 6].map((wd) => (
                    <td key={wd} style={{ ...S.td, textAlign: 'center' }}>
                      <input type="checkbox" checked={enabled(wd, s.id)} onChange={() => toggle(wd, s.id)} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function TreeTab() {
  const [nodes, setNodes] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [previewShift, setPreviewShift] = useState('early');
  const [previewWd, setPreviewWd] = useState(new Date().getDay());
  const [previewStaff, setPreviewStaff] = useState(''); // 員工實看必選
  const [previewViewMode, setPreviewViewMode] = useState('staff'); // staff | manager
  const [previewSimFirst, setPreviewSimFirst] = useState(false);
  const [previewSimLast, setPreviewSimLast] = useState(false);
  const [preview, setPreview] = useState(null);
  const [weekRows, setWeekRows] = useState([]);
  const [dragId, setDragId] = useState(null);
  const [dropHint, setDropHint] = useState(null); // { id, mode: 'before'|'into' }
  const [collapsed, setCollapsed] = useState({}); // 後台樹收合 {nodeId:true}
  const [previewCollapsed, setPreviewCollapsed] = useState({}); // 預覽收合
  const [filter, setFilter] = useState(DEFAULT_FILTER);
  const [adminTab, setAdminTab] = useState('regular'); // regular | extra | inactive

  const inactiveCount = useMemo(
    () => nodes.filter((n) => n.isActive === false).length,
    [nodes],
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api('/api/admin/shift/tree');
      if (r?.ok) setNodes(r.nodes || []);
    } finally { setLoading(false); }
  }, []);

  const loadStaff = useCallback(async () => {
    try {
      const r = await api('/api/admin/shift/staff');
      if (r?.ok) setStaffList((r.staff || []).filter((s) => s.staffCode));
    } catch { /* ignore */ }
  }, []);

  const loadWeekRows = useCallback(async () => {
    try {
      const r = await api('/api/admin/shift/week-shifts');
      if (r?.ok) setWeekRows(r.rows || []);
    } catch { /* ignore */ }
  }, []);

  const effectivePreviewWd = (previewSimFirst || previewSimLast) ? 0 : previewWd;
  const previewShiftOpts = useMemo(() => {
    const enabled = weekRows
      .filter((x) => Number(x.weekday) === effectivePreviewWd && !!x.isEnabled)
      .map((x) => x.shiftType);
    if (enabled.length) return SHIFT_OPTS.filter((s) => enabled.includes(s.id));
    if (effectivePreviewWd === 0) {
      return SHIFT_OPTS.filter((s) => s.id === 'early' || s.id === 'late' || s.id === 'maintenance');
    }
    return SHIFT_OPTS.filter((s) => s.id === 'early' || s.id === 'late' || s.id === 'night');
  }, [weekRows, effectivePreviewWd]);

  const previewShiftIds = previewShiftOpts.map((s) => s.id).join(',');

  const loadPreview = useCallback(async () => {
    try {
      if (previewViewMode === 'staff' && !previewStaff) {
        setPreview(null);
        return;
      }
      let dateStr = '';
      let wd = previewWd;
      if (previewSimFirst) {
        dateStr = firstSundayOfMonth();
        wd = 0;
      } else if (previewSimLast) {
        dateStr = lastSundayOfMonth();
        wd = 0;
      }
      const q = new URLSearchParams({
        shiftType: previewShift,
        weekday: String(wd),
        viewMode: previewViewMode,
      });
      if (dateStr) q.set('date', dateStr);
      if (previewViewMode === 'staff' && previewStaff) q.set('staffCode', previewStaff);
      const r = await api(`/api/admin/shift/tree/preview?${q.toString()}`);
      if (r?.ok) setPreview(r);
      else setPreview(null);
    } catch { setPreview(null); }
  }, [previewShift, previewWd, previewStaff, previewViewMode, previewSimFirst, previewSimLast]);

  useEffect(() => { load(); loadStaff(); loadWeekRows(); }, [load, loadStaff, loadWeekRows]);
  useEffect(() => { loadPreview(); }, [loadPreview]);

  // 員工名單載入後：實看模式若尚未選人，預設第一位
  useEffect(() => {
    if (previewViewMode !== 'staff') return;
    if (previewStaff) return;
    if (staffList.length) setPreviewStaff(String(staffList[0].staffCode));
  }, [staffList, previewStaff, previewViewMode]);

  // 切星期後若目前班別當日未開，自動改選當日第一個有開的班
  useEffect(() => {
    if (!previewShiftIds) return;
    const ids = previewShiftIds.split(',');
    if (!ids.includes(previewShift)) {
      setPreviewShift(ids[0]);
    }
  }, [previewShiftIds, previewShift]);

  const previewStaffName = useMemo(() => {
    if (previewViewMode === 'manager') return '主管總覽';
    const s = staffList.find((x) => String(x.staffCode) === String(previewStaff));
    return s ? (s.name || s.staffCode) : (previewStaff || '（未選員工）');
  }, [previewViewMode, previewStaff, staffList]);

  const previewHint = useMemo(() => {
    const shiftLbl = SHIFT_LABEL[previewShift] || previewShift;
    const wdLbl = (previewSimFirst || previewSimLast)
      ? (previewSimFirst ? '每月第一周日' : '每月最後周日')
      : `週${WD_LABEL[previewWd]}`;
    const scope = adminTab === 'extra' ? '額外' : '常態';
    return `預覽＝${shiftLbl}・${previewStaffName}・${wdLbl}會看到的${scope}`;
  }, [previewShift, previewStaffName, previewWd, previewSimFirst, previewSimLast, adminTab]);

  const save = async (data) => {
    let payload;
    if (data.assignmentsOnly) {
      payload = { assignments: data.assignments || [] };
    } else if (data.contentOnly) {
      payload = {
        title: data.title,
        nodeType: data.nodeType,
        parentId: data.parentId || null,
        description: data.description || '',
        noteText: data.noteText || '',
        noteImageUrl: data.noteImageUrl || '',
        sortOrder: Number(data.sortOrder) || 0,
      };
    } else {
      payload = {
        title: data.title,
        nodeType: data.nodeType,
        parentId: data.parentId || null,
        shiftTypes: data.shiftTypes || [],
        scheduleDays: data.scheduleDays || [0, 1, 2, 3, 4, 5, 6],
        isSpecial: !!data.isSpecial,
        extraKind: data.extraKind || '',
        targetDate: data.targetDate || null,
        targetDates: Array.isArray(data.targetDates) ? data.targetDates : [],
        targetStaffCodes: Array.isArray(data.targetStaffCodes) ? data.targetStaffCodes : [],
        assignments: Array.isArray(data.assignments) ? data.assignments : undefined,
        monthFirstSunday: !!data.monthFirstSunday,
        monthLastSunday: !!data.monthLastSunday,
        description: data.description || '',
        noteText: data.noteText || '',
        noteImageUrl: data.noteImageUrl || '',
        sortOrder: Number(data.sortOrder) || 0,
        validDays: Math.max(1, Math.min(90, Number(data.validDays) || 1)),
      };
      if (data.treeScope) payload.treeScope = data.treeScope;
      if (payload.assignments === undefined) delete payload.assignments;
    }
    let r;
    if (modal?.mode === 'edit' || data.assignmentsOnly) {
      r = await api(`/api/admin/shift/tree/${data.id}`, { method: 'PATCH', body: JSON.stringify(payload) });
    } else {
      r = await api('/api/admin/shift/tree', { method: 'POST', body: JSON.stringify(payload) });
    }
    if (r?.ok) {
      const openAssignAfter = data.openAssignAfterCreate;
      setModal(null);
      await load();
      loadPreview();
      if (openAssignAfter && r.id) {
        // 稍後由呼叫端處理；此處僅關閉
      }
    } else {
      alert(
        r?.error === 'need_migrate_v7_assignments' ? '請先執行 migrate-shift-v7-assignments.sql'
          : r?.error === 'need_migrate_v8_valid_days' ? '請先執行 migrate-shift-v8-valid-days.sql'
            : '儲存失敗',
      );
    }
    return r;
  };

  const saveAssignments = async (nodeId, assignments) => {
    const r = await api(`/api/admin/shift/tree/${nodeId}`, {
      method: 'PATCH',
      body: JSON.stringify({ assignments }),
    });
    if (r?.ok) { load(); loadPreview(); }
    else alert(r?.error === 'need_migrate_v7_assignments' ? '請先執行 migrate-shift-v7-assignments.sql' : '儲存指派失敗');
    return r;
  };

  const remove = async (id) => {
    if (!window.confirm('停用此節點及其底下所有子項？此操作會連帶停用整棵子樹。')) return;
    const r = await api(`/api/admin/shift/tree/${id}`, { method: 'DELETE' });
    if (r?.ok) { load(); loadPreview(); }
    else alert('停用失敗');
  };

  const restore = async (id) => {
    if (!window.confirm('重新啟用此節點？將一併啟用上層路徑與底下所有子項。')) return;
    const r = await api(`/api/admin/shift/tree/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ isActive: true }),
    });
    if (r?.ok) { load(); loadPreview(); }
    else alert('啟用失敗');
  };

  const listNodes = adminTab === 'inactive'
    ? nodes.filter((n) => n.isActive === false)
    : nodes.filter((n) => n.isActive !== false);
  const byParent = {};
  listNodes.forEach((n) => {
    const parentInList = n.parentId != null && listNodes.some((p) => p.id === n.parentId);
    const k = parentInList ? String(n.parentId) : 'root';
    if (!byParent[k]) byParent[k] = [];
    byParent[k].push(n);
  });
  Object.values(byParent).forEach((arr) => arr.sort((a, b) => a.sortOrder - b.sortOrder || a.id - b.id));

  const applyDrop = async (sourceId, targetId, mode) => {
    if (adminTab === 'inactive') return;
    if (!sourceId || !targetId || sourceId === targetId) return;
    const source = listNodes.find((n) => n.id === sourceId);
    const target = listNodes.find((n) => n.id === targetId);
    if (!source || !target) return;
    const srcScope = source.treeScope === 'extra' ? 'extra' : 'regular';
    const tgtScope = target.treeScope === 'extra' ? 'extra' : 'regular';
    if (adminTab === 'regular' && srcScope !== 'regular') return;
    if (adminTab === 'extra' && srcScope !== 'extra') return;

    let newParentId;
    let siblings;
    if (mode === 'into' && target.nodeType === 'category') {
      if (tgtScope !== srcScope) return;
      newParentId = target.id;
      siblings = [...(byParent[String(target.id)] || [])].filter((n) => n.id !== sourceId);
      siblings.push({ ...source, parentId: newParentId });
    } else {
      const targetParentInList = target.parentId != null && listNodes.some((p) => p.id === target.parentId);
      newParentId = targetParentInList ? target.parentId : null;
      siblings = [...(byParent[newParentId == null ? 'root' : String(newParentId)] || [])].filter((n) => n.id !== sourceId);
      const idx = siblings.findIndex((n) => n.id === targetId);
      siblings.splice(Math.max(0, idx), 0, { ...source, parentId: newParentId });
    }

    const orders = siblings.map((n, i) => ({
      id: n.id,
      parentId: newParentId,
      sortOrder: i,
    }));
    const r = await api('/api/admin/shift/tree/reorder', { method: 'POST', body: JSON.stringify({ orders }) });
    if (r?.ok) { load(); loadPreview(); }
    else alert('排序失敗');
  };

  // 單一節點是否符合篩選條件（不含子孫）
  const matchNode = (n) => {
    const assigns = getNodeAssignments(n);
    const isPerson = assigns.some((a) => a.kind === 'person') || (n.targetStaffCodes || []).length > 0;
    const isShiftExtra = assigns.some((a) => a.kind === 'shift')
      || (!!n.isSpecial && !isPerson);
    const isExtraScope = n.treeScope === 'extra' || assigns.length > 0 || isPerson || isShiftExtra;
    const isRegular = !isExtraScope;
    if (adminTab !== 'inactive') {
      if (adminTab === 'regular' && !isRegular && n.nodeType !== 'category') return false;
      if (adminTab === 'extra' && !isExtraScope && n.nodeType !== 'category') return false;
      if (adminTab === 'extra' && filter.extraKind === 'shift' && !isShiftExtra && n.nodeType !== 'category') return false;
      if (adminTab === 'extra' && filter.extraKind === 'person' && !isPerson && n.nodeType !== 'category') return false;
      if (filter.specialOnly && !isExtraScope) return false;
    }
    if (filter.nodeType && n.nodeType !== filter.nodeType) return false;
    if (filter.shiftType) {
      const st = assigns.filter((a) => a.kind === 'shift').map((a) => a.shiftType);
      const legacy = n.shiftTypes || [];
      const pool = st.length ? st : legacy;
      if (pool.length && !pool.includes(filter.shiftType)) return false;
    }
    if (filter.weekdays.length) {
      const days = n.scheduleDays || [];
      if (days.length && !filter.weekdays.some((d) => days.includes(d))) return false;
    }
    if (filter.keyword) {
      const kw = filter.keyword.trim().toLowerCase();
      if (kw && !String(n.title || '').toLowerCase().includes(kw)) return false;
    }
    return true;
  };
  const hasActiveFilter = !!(filter.shiftType || filter.nodeType || filter.specialOnly || filter.weekdays.length || filter.keyword.trim() || filter.extraKind);
  // 節點或其任一子孫符合篩選 → 顯示（保留樹狀結構）；常態／額外分頁一律套用
  const subtreeMatches = (n) => {
    if (matchNode(n)) return true;
    return (byParent[String(n.id)] || []).some((c) => subtreeMatches(c));
  };
  const visibleInTab = (n) => {
    if (adminTab === 'inactive') return subtreeMatches(n);
    // 額外分頁：分類只在有符合的子孫時顯示，避免空白分類干擾查過去紀錄
    if (adminTab === 'extra' && n.nodeType === 'category') {
      return (byParent[String(n.id)] || []).some((c) => subtreeMatches(c));
    }
    return subtreeMatches(n);
  };

  const parentTitleById = useMemo(() => {
    const m = {};
    for (const n of nodes) m[n.id] = n.title;
    return m;
  }, [nodes]);

  const staffNameByCode = useMemo(() => {
    const m = {};
    for (const s of staffList) m[String(s.staffCode)] = s.name || s.staffCode;
    return m;
  }, [staffList]);

  const todayStr = todayYmd();

  const formatExtraDates = (n) => {
    const assigns = getNodeAssignments(n);
    const dates = [...new Set(assigns.map((a) => a.date))].sort();
    if (!dates.length) return <span style={{ color: '#9ca3af', fontSize: 12 }}>未設日期</span>;
    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
        {dates.map((dt) => {
          const past = dt < todayStr;
          const isToday = dt === todayStr;
          const count = assigns.filter((a) => a.date === dt).length;
          return (
            <span
              key={dt}
              style={{
                padding: '2px 7px', borderRadius: 6, fontSize: 11, fontWeight: 700,
                background: isToday ? '#ffedd5' : past ? '#f3f4f6' : '#fef3c7',
                color: isToday ? '#c2410c' : past ? '#6b7280' : '#92400e',
                textDecoration: past ? 'line-through' : 'none',
              }}
              title={`${count} 筆指派`}
            >{dt}{past ? '（過）' : isToday ? '（今）' : ''} · {count}</span>
          );
        })}
      </div>
    );
  };

  const formatExtraTarget = (n) => {
    const assigns = getNodeAssignments(n);
    if (!assigns.length) return <span style={{ color: '#9ca3af', fontSize: 12 }}>未指定</span>;
    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
        {assigns.slice(0, 8).map((a) => (
          <span key={a.id} style={{
            padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 700,
            background: a.kind === 'person' ? '#f3e8ff' : '#ffedd5',
            color: a.kind === 'person' ? '#7c3aed' : '#c2410c',
          }}>
            {a.date.slice(5)} · {a.kind === 'person'
              ? (staffNameByCode[String(a.staffCode)] || a.staffCode)
              : (SHIFT_LABEL[a.shiftType] || a.shiftType)}
          </span>
        ))}
        {assigns.length > 8 ? <span style={{ fontSize: 11, color: '#9ca3af' }}>＋{assigns.length - 8}</span> : null}
      </div>
    );
  };

  const renderRows = (parentKey, depth) => {
    let list = [...(byParent[parentKey] || [])];
    // 額外分頁：依最近指定日新→舊，方便查過去分配
    if (adminTab === 'extra' && parentKey === 'root') {
      const maxDate = (n) => {
        const ds = getNodeAssignments(n).map((a) => a.date);
        return ds.length ? ds.sort().slice(-1)[0] : '';
      };
      list.sort((a, b) => {
        const da = maxDate(a);
        const db = maxDate(b);
        if (da !== db) return db.localeCompare(da);
        return (a.sortOrder - b.sortOrder) || (a.id - b.id);
      });
    }
    return list.flatMap((n) => {
    if (!visibleInTab(n)) return [];
    const hint = dropHint?.id === n.id ? dropHint.mode : null;
    const isCat = n.nodeType === 'category';
    const childKeys = byParent[String(n.id)] || [];
    const hasChildren = childKeys.length > 0;
    const isCollapsed = !!collapsed[n.id];
    const selfDim = hasActiveFilter && !matchNode(n) ? 0.5 : 1;
    const parentMissing = n.parentId != null && !listNodes.some((p) => p.id === n.parentId);
    const parentLabel = n.parentId != null ? (parentTitleById[n.parentId] || `#${n.parentId}`) : null;
    const canDrag = adminTab !== 'inactive';
    const row = (
      <tr
        key={n.id}
        draggable={canDrag}
        onDragStart={canDrag ? (e) => {
          setDragId(n.id);
          e.dataTransfer.effectAllowed = 'move';
          e.dataTransfer.setData('text/plain', String(n.id));
        } : undefined}
        onDragEnd={canDrag ? () => { setDragId(null); setDropHint(null); } : undefined}
        onDragOver={canDrag ? (e) => {
          e.preventDefault();
          if (!dragId || dragId === n.id) return;
          const rect = e.currentTarget.getBoundingClientRect();
          const mid = rect.top + rect.height / 2;
          const nextMode = (n.nodeType === 'category' && e.clientY >= mid) ? 'into' : 'before';
          setDropHint({ id: n.id, mode: nextMode });
        } : undefined}
        onDrop={canDrag ? (e) => {
          e.preventDefault();
          const sid = Number(e.dataTransfer.getData('text/plain') || dragId);
          const mode = dropHint?.id === n.id ? dropHint.mode : 'before';
          setDropHint(null);
          setDragId(null);
          applyDrop(sid, n.id, mode);
        } : undefined}
        style={{
          opacity: dragId === n.id ? 0.45 : selfDim,
          outline: hint === 'into' ? '2px solid #0369a1' : (hint === 'before' ? '2px solid #7b4f2f' : undefined),
          background: adminTab === 'inactive' ? '#f9fafb' : (hint ? '#f8fafc' : undefined),
          cursor: canDrag ? 'grab' : 'default',
        }}
      >
        <td style={{ ...S.td, paddingLeft: 10 + depth * 22 }}>
          {canDrag ? <span style={{ color: '#9ca3af', marginRight: 6, cursor: 'grab' }}>⋮⋮</span> : null}
          {isCat && hasChildren ? (
            <button
              onClick={() => setCollapsed((c) => ({ ...c, [n.id]: !c[n.id] }))}
              style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: 13, color: '#374151', marginRight: 4, padding: 0, width: 16 }}
              title={isCollapsed ? '展開' : '收合'}
            >
              {isCollapsed ? '▸' : '▾'}
            </button>
          ) : (
            <span style={{ display: 'inline-block', width: 20 }} />
          )}
          <span style={{ fontWeight: isCat ? 800 : 500, color: adminTab === 'inactive' ? '#6b7280' : undefined }}>{n.title}</span>
          {isCollapsed && hasChildren ? <span style={{ color: '#9ca3af', fontSize: 12, marginLeft: 6 }}>（{childKeys.length}）</span> : null}
          {adminTab === 'inactive' ? <Tag bg="#f3f4f6" color="#6b7280">已停用</Tag> : null}
          {adminTab === 'inactive' && parentMissing && parentLabel ? (
            <Tag bg="#fee2e2" color="#b91c1c">原上層：{parentLabel}</Tag>
          ) : null}
          {n.isSpecial || getNodeAssignments(n).length ? <Tag bg="#ffedd5" color="#c2410c">日期額外</Tag> : null}
          {!n.isSpecial && !getNodeAssignments(n).length && n.nodeType !== 'category' ? <Tag bg="#e0f2fe" color="#0369a1">常態</Tag> : null}
          {n.monthFirstSunday ? <Tag bg="#dbeafe" color="#1d4ed8">每月第一周日</Tag> : null}
          {n.monthLastSunday ? <Tag bg="#e0e7ff" color="#4338ca">每月最後周日</Tag> : null}
          {!n.isSpecial && (Number(n.validDays) || 1) > 1 ? <Tag bg="#ffedd5" color="#c2410c">每{Number(n.validDays)}日</Tag> : null}
          {(n.noteText || n.noteImageUrl) ? <Tag bg="#ecfdf5" color="#047857">有附註</Tag> : null}
          {hint === 'into' ? <Tag bg="#e0f2fe" color="#0369a1">放入此分類</Tag> : null}
        </td>
        <td style={S.td}><TypeTag type={n.nodeType} /></td>
        {adminTab === 'extra' ? (
          <>
            <td style={S.td}>{isCat ? <span style={{ color: '#9ca3af', fontSize: 12 }}>—</span> : formatExtraDates(n)}</td>
          </>
        ) : (
          <>
            <td style={S.td}>{(n.shiftTypes || []).length ? (n.shiftTypes || []).map((s) => SHIFT_LABEL[s] || s).join('、') : '全部'}</td>
            <td style={S.td}>
              {n.isSpecial && (n.targetDates || []).length
                ? <span style={{ fontSize: 12, color: '#b45309', fontWeight: 700 }}>{n.targetDates.length} 個指定日</span>
                : (n.isSpecial && n.targetDate ? <span style={{ fontSize: 12, color: '#b45309' }}>{n.targetDate}</span> : <WeekdayDisplay days={n.scheduleDays} />)}
            </td>
          </>
        )}
        <td style={S.td}>{n.sortOrder}</td>
        <td style={S.td}>
          {adminTab === 'inactive' ? (
            <button style={{ ...S.btnGhost, color: '#166534' }} onClick={() => restore(n.id)}>重新啟用</button>
          ) : (
            <>
              {adminTab === 'extra' && !isCat ? (
                <>
                  <button style={S.btnGhost} onClick={() => setModal({
                    mode: 'edit',
                    formScope: 'extra-content',
                    data: { ...n },
                  })}>編輯內容</button>
                  <button style={{ ...S.btnGhost, color: '#c2410c', marginLeft: 4 }} onClick={() => setModal({
                    mode: 'assignments',
                    data: { ...n },
                  })}>任務指派</button>
                </>
              ) : (
                <button style={S.btnGhost} onClick={() => setModal({
                  mode: 'edit',
                  formScope: (n.targetStaffCodes || []).length || n.isSpecial || getNodeAssignments(n).length ? 'extra-content' : 'regular',
                  data: { ...n, shiftTypes: n.shiftTypes || [], scheduleDays: n.scheduleDays || [] },
                })}>編輯</button>
              )}
              <button style={{ ...S.btnGhost, color: '#b91c1c', marginLeft: 4 }} onClick={() => remove(n.id)}>停用</button>
              {n.nodeType === 'category' && adminTab === 'regular' && (
                <button style={{ ...S.btnGhost, marginLeft: 4 }} onClick={() => setModal({ mode: 'create', formScope: 'regular', data: { title: '', nodeType: 'check', parentId: n.id, shiftTypes: n.shiftTypes || [], scheduleDays: n.scheduleDays || [0,1,2,3,4,5,6], sortOrder: 0, isSpecial: false, taskMode: 'regular', extraKind: '', targetDates: [], targetStaffCodes: [], assignments: [], validDays: 1, monthFirstSunday: false, monthLastSunday: false } })}>＋子項</button>
              )}
            </>
          )}
        </td>
      </tr>
    );
    if (isCollapsed) return [row];
    return [row, ...renderRows(String(n.id), depth + 1)];
  });
  };

  const previewBlocks = useMemo(() => {
    if (adminTab === 'extra') {
      const blocks = [
        ...((preview?.shiftExtra || []).length
          ? [{ label: '班別額外', nodes: preview.shiftExtra, accent: 'shift' }]
          : []),
        ...((preview?.personalExtra || []).length
          ? [{ label: '個人額外', nodes: preview.personalExtra, accent: 'person' }]
          : []),
      ];
      return blocks.length ? blocks : [{ label: '額外代辦', nodes: [], accent: 'shift' }];
    }
    return [{ label: '班別待辦', nodes: preview?.regular || [] }];
  }, [adminTab, preview]);

  const collapseAll = () => {
    const next = {};
    for (const n of listNodes) if (n.nodeType === 'category') next[n.id] = true;
    setCollapsed(next);
  };

  return (
    <div className="shift-tree-admin-wrap">
      <WeekShiftMatrix onSaved={(rows) => { setWeekRows(rows || []); }} />
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <button
          type="button"
          style={{
            ...S.btn(adminTab === 'regular' ? '#0369a1' : '#94a3b8'),
          }}
          onClick={() => { setAdminTab('regular'); setFilter((f) => ({ ...f, extraKind: '', specialOnly: false })); }}
        >常態任務</button>
        <button
          type="button"
          style={{
            ...S.btn(adminTab === 'extra' ? '#c2410c' : '#94a3b8'),
          }}
          onClick={() => { setAdminTab('extra'); setFilter((f) => ({ ...f, specialOnly: true })); }}
        >額外代辦</button>
        <button
          type="button"
          style={{
            ...S.btn(adminTab === 'inactive' ? '#475569' : '#94a3b8'),
          }}
          onClick={() => { setAdminTab('inactive'); setFilter((f) => ({ ...f, extraKind: '', specialOnly: false })); }}
        >已停用{inactiveCount ? `（${inactiveCount}）` : ''}</button>
      </div>
    <div className="shift-tree-admin-grid" style={{ display: 'grid', gridTemplateColumns: adminTab === 'inactive' ? '1fr' : '1fr minmax(280px, 400px)', gap: 16, alignItems: 'start' }}>
      <div>
        {/* 任務指派一覽：各班葉節點數／綁定員工數 */}
        {!loading && adminTab === 'regular' && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 8, marginBottom: 12 }}>
            {SHIFT_OPTS.map((s) => {
              const leaves = nodes.filter((n) => (n.nodeType === 'check' || n.nodeType === 'text') && n.isActive !== false && !n.isSpecial && !(n.targetStaffCodes || []).length);
              const forShift = leaves.filter((n) => {
                const st = n.shiftTypes || [];
                return !st.length || st.includes(s.id);
              });
              return (
                <div key={s.id} style={{
                  background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '10px 12px',
                  borderTop: `3px solid ${SHIFT_COLOR[s.id] || '#64748b'}`,
                }}>
                  <div style={{ fontSize: 12, color: '#6b7280' }}>{s.label} · 常態</div>
                  <div style={{ fontSize: 20, fontWeight: 800, color: '#111827' }}>{forShift.length}</div>
                </div>
              );
            })}
          </div>
        )}

        <div style={{ marginBottom: 12, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          {adminTab === 'regular' ? (
            <>
              <button style={S.btn()} onClick={() => setModal({ mode: 'create', formScope: 'regular', data: { title: '', nodeType: 'category', parentId: null, shiftTypes: [], scheduleDays: [0,1,2,3,4,5,6], sortOrder: 0, isSpecial: false, taskMode: 'regular', extraKind: '', targetDates: [], targetStaffCodes: [], monthFirstSunday: false, monthLastSunday: false, validDays: 1 } })}>＋ 根分類</button>
              <button style={S.btn('#0369a1')} onClick={() => setModal({ mode: 'create', formScope: 'regular', data: { title: '', nodeType: 'check', parentId: null, shiftTypes: [], scheduleDays: [0,1,2,3,4,5,6], sortOrder: 0, isSpecial: false, taskMode: 'regular', extraKind: '', targetDates: [], targetStaffCodes: [], monthFirstSunday: false, monthLastSunday: false, validDays: 1 } })}>＋ 常態任務</button>
            </>
          ) : adminTab === 'extra' ? (
            <>
              <button style={S.btn('#c2410c')} onClick={() => setModal({
                mode: 'create',
                formScope: 'extra-category',
                data: {
                  title: '', nodeType: 'category', parentId: null, sortOrder: 0, treeScope: 'extra',
                  shiftTypes: [], scheduleDays: [0, 1, 2, 3, 4, 5, 6],
                },
              })}>＋ 根分類</button>
              <button style={S.btn('#ea580c')} onClick={() => setModal({
                mode: 'create',
                formScope: 'extra-create',
                data: {
                  title: '', nodeType: 'check', parentId: null, sortOrder: 0, isSpecial: true, treeScope: 'extra',
                  noteText: '', noteImageUrl: '', description: '',
                  firstAssign: { date: todayYmd(), kind: 'shift', shiftType: 'early', staffCode: '' },
                },
              })}>＋ 新增額外任務</button>
            </>
          ) : (
            <span style={{ fontSize: 13, color: '#6b7280' }}>管理已停用節點；重新啟用會連帶上層路徑與底下子項</span>
          )}
          <button style={S.btnGhost} onClick={collapseAll}>全部收合</button>
          <button style={S.btnGhost} onClick={() => setCollapsed({})}>全部展開</button>
        </div>

        <TaskFilter filter={filter} onChange={setFilter} showExtraKind={adminTab === 'extra'} />

        <div style={S.card}>
          {loading ? <div style={{ padding: 20, color: '#888' }}>載入中…</div> : (
            listNodes.length === 0 && adminTab === 'inactive' ? (
              <div style={{ padding: 20, color: '#9ca3af' }}>目前沒有已停用的任務</div>
            ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: adminTab === 'extra' ? 720 : 640 }}>
                <thead>
                  <tr>
                    <th style={S.th}>標題</th>
                    <th style={S.th}>類型</th>
                    {adminTab === 'extra' ? (
                      <>
                        <th style={S.th}>指定日期</th>
                      </>
                    ) : (
                      <>
                        <th style={S.th}>班別</th>
                        <th style={S.th}>週別／指定日</th>
                      </>
                    )}
                    <th style={S.th}>排序</th>
                    <th style={S.th}>操作</th>
                  </tr>
                </thead>
                <tbody>{renderRows('root', 0)}</tbody>
              </table>
            </div>
            )
          )}
        </div>
      </div>

      {adminTab !== 'inactive' ? (
      <div style={{ ...S.card, position: 'sticky', top: 8, padding: 12 }}>
        <div style={{ ...S.h, marginBottom: 8 }}>預覽</div>

        <div style={{ fontSize: 11, color: '#64748b', marginBottom: 6 }}>查看身分</div>
        <div style={{ display: 'flex', gap: 6, marginBottom: 8, flexWrap: 'wrap' }}>
          <button type="button" style={subPreviewBtn(previewViewMode === 'staff')} onClick={() => setPreviewViewMode('staff')}>員工實看</button>
          <button type="button" style={subPreviewBtn(previewViewMode === 'manager')} onClick={() => setPreviewViewMode('manager')}>主管總覽</button>
        </div>

        {previewViewMode === 'staff' ? (
          <div style={{ marginBottom: 10 }}>
            <select
              style={{ ...S.input, width: '100%', fontSize: 12, padding: '6px 8px' }}
              value={previewStaff}
              onChange={(e) => setPreviewStaff(e.target.value)}
            >
              {!staffList.length ? <option value="">尚無員工</option> : null}
              {staffList.map((s) => (
                <option key={s.staffCode} value={s.staffCode}>{s.name}（{s.staffCode}）</option>
              ))}
            </select>
          </div>
        ) : (
          <div style={{ fontSize: 11, color: '#9a3412', marginBottom: 10 }}>主管總覽：略過個人過濾，可見該日該班相關額外</div>
        )}

        <div style={{ display: 'flex', gap: 4, marginBottom: 8, flexWrap: 'wrap' }}>
          {WD_LABEL.map((l, i) => (
            <button
              key={i}
              style={subPreviewBtn(previewWd === i && !previewSimFirst && !previewSimLast)}
              disabled={previewSimFirst || previewSimLast}
              onClick={() => { setPreviewSimFirst(false); setPreviewSimLast(false); setPreviewWd(i); }}
            >{l}</button>
          ))}
        </div>
        <div style={{ fontSize: 11, color: '#64748b', marginBottom: 6 }}>當日有開班別（依星期×班別）</div>
        <div style={{ display: 'flex', gap: 6, marginBottom: 8, flexWrap: 'wrap' }}>
          {previewShiftOpts.length === 0 ? (
            <span style={{ fontSize: 12, color: '#b91c1c' }}>此日無任何班別</span>
          ) : previewShiftOpts.map((s) => (
            <button key={s.id} style={subPreviewBtn(previewShift === s.id)} onClick={() => setPreviewShift(s.id)}>{s.label}</button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 12, marginBottom: 8, flexWrap: 'wrap', fontSize: 12 }}>
          <label style={{ display: 'inline-flex', gap: 4, alignItems: 'center', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={previewSimFirst}
              onChange={(e) => {
                const on = e.target.checked;
                setPreviewSimFirst(on);
                if (on) { setPreviewSimLast(false); setPreviewWd(0); }
              }}
            />
            模擬第一周日
          </label>
          <label style={{ display: 'inline-flex', gap: 4, alignItems: 'center', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={previewSimLast}
              onChange={(e) => {
                const on = e.target.checked;
                setPreviewSimLast(on);
                if (on) { setPreviewSimFirst(false); setPreviewWd(0); }
              }}
            />
            模擬最後周日
          </label>
        </div>

        <div style={{ fontSize: 12, fontWeight: 700, color: '#0369a1', marginBottom: 8, lineHeight: 1.4 }}>
          {previewHint}
        </div>
        {preview?.bizDate ? (
          <div style={{ fontSize: 11, color: '#64748b', marginBottom: 8 }}>
            預覽營業日：{preview.bizDate}（{WD_LABEL[preview.weekday] || ''}）
            {preview.shiftAvailable === false ? ' · 此日無此班' : ''}
          </div>
        ) : null}

        {previewViewMode === 'staff' && !previewStaff ? (
          <div style={{ color: '#9ca3af', fontSize: 13, padding: '8px 0' }}>請選擇預覽員工</div>
        ) : preview?.shiftAvailable === false ? (
          <div style={{ color: '#b91c1c', fontSize: 13, padding: '8px 0' }}>此星期未開此班別，預覽無任務（與前台一致）</div>
        ) : (
          <div className="shift-root shift-admin-preview">
            <div className="shift-phone">
              <div className="shift-scroll">
                <ShiftTreeView
                  blocks={previewBlocks}
                  collapsed={previewCollapsed}
                  setCollapsed={setPreviewCollapsed}
                  readOnly
                  showToolbar
                />
              </div>
            </div>
          </div>
        )}
      </div>
      ) : null}

      {modal?.mode === 'assignments' && (
        <ExtraAssignmentsPanel
          node={nodes.find((x) => x.id === modal.data.id) || modal.data}
          staffList={staffList}
          onClose={() => setModal(null)}
          onSave={async (assignments) => {
            const r = await saveAssignments(modal.data.id, assignments);
            if (r?.ok) setModal(null);
          }}
        />
      )}
      {modal && modal.mode !== 'assignments' && (
        <TreeNodeModal
          mode={modal.mode}
          formScope={modal.formScope || 'regular'}
          initial={modal.data}
          nodes={nodes}
          staffList={staffList}
          onClose={() => setModal(null)}
          onSave={async (data) => {
            const r = await save(data);
            if (r?.ok && data.openAssignAfterCreate && r.id) {
              setModal({
                mode: 'assignments',
                data: {
                  id: r.id,
                  title: data.title,
                  assignments: data.assignments || [],
                  isSpecial: true,
                },
              });
            }
          }}
        />
      )}
    </div>
    </div>
  );
}

function TaskFilter({ filter, onChange, showExtraKind = false }) {
  const toggleWd = (d) => {
    const set = new Set(filter.weekdays);
    if (set.has(d)) set.delete(d); else set.add(d);
    onChange({ ...filter, weekdays: [...set].sort((a, b) => a - b) });
  };
  return (
    <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10, padding: 12, marginBottom: 16 }}>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        {showExtraKind && (
          <div style={{ minWidth: 140 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 4, display: 'block' }}>額外</label>
            <span style={{ fontSize: 13, fontWeight: 600, color: '#9a3412' }}>只綁日期</span>
          </div>
        )}
        <div style={{ minWidth: 120 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 4, display: 'block' }}>班別</label>
          <select value={filter.shiftType} onChange={(e) => onChange({ ...filter, shiftType: e.target.value })} style={{ ...S.input, width: '100%' }}>
            <option value="">全部</option>
            <option value="early">早班</option>
            <option value="late">晚班</option>
            <option value="night">夜班</option>
            <option value="maintenance">維護班</option>
          </select>
        </div>
        <div style={{ minWidth: 120 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 4, display: 'block' }}>類型</label>
          <select value={filter.nodeType} onChange={(e) => onChange({ ...filter, nodeType: e.target.value })} style={{ ...S.input, width: '100%' }}>
            <option value="">全部</option>
            <option value="category">分類</option>
            <option value="check">核選</option>
            <option value="text">填空</option>
          </select>
        </div>
        <div style={{ flex: 1, minWidth: 160 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 4, display: 'block' }}>關鍵字</label>
          <input value={filter.keyword} onChange={(e) => onChange({ ...filter, keyword: e.target.value })} placeholder="搜尋標題" style={{ ...S.input, width: '100%' }} />
        </div>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer', paddingBottom: 8 }}>
          <input type="checkbox" checked={filter.specialOnly} onChange={(e) => onChange({ ...filter, specialOnly: e.target.checked })} />
          <span style={{ fontSize: 13, fontWeight: 600 }}>僅額外待辦</span>
        </label>
      </div>
      <div style={{ marginTop: 10, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: '#6b7280' }}>週別</span>
        {WD_LABEL.map((l, d) => (
          <button
            key={d}
            type="button"
            onClick={() => toggleWd(d)}
            style={{
              padding: '3px 10px', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer',
              border: filter.weekdays.includes(d) ? '1px solid #4f46e5' : '1px solid #d1d5db',
              background: filter.weekdays.includes(d) ? '#4f46e5' : '#fff',
              color: filter.weekdays.includes(d) ? '#fff' : '#374151',
            }}
          >{l}</button>
        ))}
        {(filter.shiftType || filter.nodeType || filter.specialOnly || filter.weekdays.length || filter.keyword) ? (
          <button type="button" onClick={() => onChange({ ...DEFAULT_FILTER })} style={{ ...S.btnGhost, marginLeft: 'auto', fontSize: 12, padding: '4px 10px' }}>清除篩選</button>
        ) : null}
      </div>
    </div>
  );
}

function subPreviewBtn(on) {
  return {
    padding: '4px 10px', borderRadius: 6, border: on ? '1px solid #5e3a1a' : '1px solid #d1d5db',
    background: on ? '#7b4f2f' : '#fff', color: on ? '#fff' : '#374151', fontSize: 12, fontWeight: 700, cursor: 'pointer',
  };
}

function ExtraAssignmentsPanel({ node, onClose, onSave }) {
  const todayStr = todayYmd();
  const [list, setList] = useState(() => getNodeAssignments(node));
  const [newDate, setNewDate] = useState(todayStr);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setList(getNodeAssignments(node));
  }, [node]);

  const dates = useMemo(() => [...new Set(list.map((a) => a.date))].sort((a, b) => b.localeCompare(a)), [list]);

  const persist = async (next) => {
    if (!next.length) { alert('至少保留一個日期'); return; }
    setSaving(true);
    try {
      await onSave(next.map((d) => ({ id: d.id || newAssignId(), date: d.date, kind: 'day' })));
    } finally { setSaving(false); }
  };

  const addDate = () => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(newDate)) { alert('請選日期'); return; }
    if (dates.includes(newDate)) return;
    const next = [...list, { id: newAssignId(), date: newDate, kind: 'day' }];
    setList(next);
    persist(next);
  };

  const removeDate = (dt) => {
    if (!window.confirm(`刪除 ${dt}？`)) return;
    const next = list.filter((x) => x.date !== dt);
    setList(next);
    persist(next);
  };

  return (
    <Modal title={`任務日期 — ${node.title || ''}`} onClose={onClose} maxWidth={520}>
      <div style={{ display: 'grid', gap: 12 }}>
        <div style={{ fontSize: 13, color: '#6b7280' }}>日期在名單裡＝當日所有上班的人都看得到</div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <input type="date" style={S.input} value={newDate} onChange={(e) => setNewDate(e.target.value)} />
          <button type="button" style={S.btn('#c2410c')} disabled={saving} onClick={addDate}>＋ 加日期</button>
        </div>
        {!dates.length ? (
          <div style={{ padding: 24, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>尚無日期</div>
        ) : dates.map((dt) => (
          <div key={dt} style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '8px 12px', border: '1px solid #e5e7eb', borderRadius: 8,
            background: dt === todayStr ? '#fff7ed' : '#fff',
          }}>
            <span style={{ fontWeight: 800 }}>{dt}{dt === todayStr ? '（今日）' : ''}</span>
            <button type="button" style={{ ...S.btnGhost, color: '#b91c1c' }} disabled={saving} onClick={() => removeDate(dt)}>刪除</button>
          </div>
        ))}
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button style={S.btnGhost} onClick={onClose} disabled={saving}>關閉</button>
        </div>
      </div>
    </Modal>
  );
}

function AssignRowForm({ mode, initial, staffList, onCancel, onSubmit }) {
  const [row, setRow] = useState({ ...initial });
  const set = (k, v) => setRow((s) => ({ ...s, [k]: v }));
  const submit = () => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(row.date || '')) { alert('請選日期'); return; }
    if (row.kind === 'person') {
      if (!row.staffCode) { alert('請指定員工'); return; }
      onSubmit({ id: row.id || newAssignId(), date: row.date, kind: 'person', staffCode: String(row.staffCode) });
    } else {
      if (!row.shiftType) { alert('請指定班別'); return; }
      onSubmit({ id: row.id || newAssignId(), date: row.date, kind: 'shift', shiftType: row.shiftType });
    }
  };
  return (
    <div style={{ background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 10, padding: 12, display: 'grid', gap: 10 }}>
      <div style={{ fontWeight: 800, color: '#9a3412', fontSize: 13 }}>{mode === 'edit' ? '編輯指派' : '新增指派'}</div>
      <div>
        <label style={S.label}>日期</label>
        <input type="date" style={S.input} value={row.date || ''} onChange={(e) => set('date', e.target.value)} />
      </div>
      <div>
        <label style={S.label}>類型</label>
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" onClick={() => set('kind', 'shift')}
            style={{ padding: '6px 12px', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer', border: row.kind === 'shift' ? '1px solid #c2410c' : '1px solid #d1d5db', background: row.kind === 'shift' ? '#c2410c' : '#fff', color: row.kind === 'shift' ? '#fff' : '#374151' }}>日＋班</button>
          <button type="button" onClick={() => set('kind', 'person')}
            style={{ padding: '6px 12px', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer', border: row.kind === 'person' ? '1px solid #7c3aed' : '1px solid #d1d5db', background: row.kind === 'person' ? '#7c3aed' : '#fff', color: row.kind === 'person' ? '#fff' : '#374151' }}>日＋人</button>
        </div>
      </div>
      {row.kind === 'shift' ? (
        <div>
          <label style={S.label}>班別</label>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {SHIFT_OPTS.map((s) => (
              <label key={s.id} style={{ display: 'inline-flex', gap: 4, alignItems: 'center', fontSize: 14 }}>
                <input type="radio" name="assignShift" checked={row.shiftType === s.id} onChange={() => set('shiftType', s.id)} />
                {s.label}
              </label>
            ))}
          </div>
        </div>
      ) : (
        <div>
          <label style={S.label}>員工</label>
          <select style={{ ...S.input, width: '100%' }} value={row.staffCode || ''} onChange={(e) => set('staffCode', e.target.value)}>
            <option value="">請選擇</option>
            {staffList.map((s) => <option key={s.staffCode} value={s.staffCode}>{s.name}（{s.staffCode}）</option>)}
          </select>
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <button type="button" style={S.btnGhost} onClick={onCancel}>取消</button>
        <button type="button" style={S.btn('#c2410c')} onClick={submit}>套用</button>
      </div>
    </div>
  );
}

function TreeNodeModal({ mode, formScope = 'regular', initial, nodes, staffList = [], onClose, onSave }) {
  const isExtraContent = formScope === 'extra-content';
  const isExtraCreate = formScope === 'extra-create';
  const isExtraCategory = formScope === 'extra-category';
  const [d, setD] = useState({
    ...initial,
    shiftTypes: Array.isArray(initial.shiftTypes) ? initial.shiftTypes : [],
    scheduleDays: Array.isArray(initial.scheduleDays) ? initial.scheduleDays : [0, 1, 2, 3, 4, 5, 6],
    monthFirstSunday: !!initial.monthFirstSunday,
    monthLastSunday: !!initial.monthLastSunday,
    validDays: Math.max(1, Math.min(90, Number(initial.validDays) || 1)),
    description: initial.description || '',
    noteText: initial.noteText || '',
    noteImageUrl: initial.noteImageUrl || '',
  });
  const [firstAssign, setFirstAssign] = useState(() => initial.firstAssign || {
    date: todayYmd(), kind: 'shift', shiftType: 'early', staffCode: '',
  });
  const [imgUploading, setImgUploading] = useState(false);
  const noteImgRef = useRef(null);
  const set = (k, v) => setD((s) => ({ ...s, [k]: v }));
  const extraCats = nodes.filter((n) => n.nodeType === 'category' && n.treeScope === 'extra' && n.isActive !== false && n.id !== d.id);
  const cats = (isExtraCreate || isExtraCategory)
    ? extraCats
    : nodes.filter((n) => n.nodeType === 'category' && n.treeScope !== 'extra' && n.isActive !== false && n.id !== d.id);
  const isLeaf = d.nodeType === 'check' || d.nodeType === 'text';
  const toggleShift = (id) => {
    const cur = new Set(d.shiftTypes || []);
    if (cur.has(id)) cur.delete(id); else cur.add(id);
    set('shiftTypes', [...cur]);
  };

  const onPickNoteImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setImgUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch('/api/admin/shift/tree/image', {
        method: 'POST',
        headers: { Authorization: `Bearer ${adminToken()}` },
        body: fd,
      });
      const r = await res.json();
      if (r?.ok && r.url) set('noteImageUrl', r.url);
      else alert('圖片上傳失敗');
    } catch { alert('圖片上傳失敗'); }
    finally { setImgUploading(false); }
  };

  const handleSave = () => {
    if (!(d.title || '').trim()) { alert('請填標題'); return; }
    if (isExtraContent) {
      onSave({
        ...d,
        contentOnly: true,
        id: d.id,
      });
      return;
    }
    if (isExtraCreate) {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(firstAssign.date || '')) { alert('請填第一筆日期'); return; }
      const assignment = { id: newAssignId(), date: firstAssign.date, kind: 'day' };
      onSave({
        title: d.title,
        nodeType: d.nodeType,
        parentId: d.parentId || null,
        description: d.description || '',
        noteText: d.noteText || '',
        noteImageUrl: d.noteImageUrl || '',
        sortOrder: Number(d.sortOrder) || 0,
        isSpecial: true,
        treeScope: 'extra',
        extraKind: 'day',
        assignments: [assignment],
        openAssignAfterCreate: true,
      });
      return;
    }
    if (isExtraCategory) {
      onSave({
        title: d.title,
        nodeType: 'category',
        parentId: d.parentId || null,
        sortOrder: Number(d.sortOrder) || 0,
        treeScope: 'extra',
        shiftTypes: [],
        scheduleDays: [0, 1, 2, 3, 4, 5, 6],
      });
      return;
    }
    // regular
    const out = {
      ...d,
      isSpecial: false,
      extraKind: '',
      targetDate: null,
      targetDates: [],
      targetStaffCodes: [],
      assignments: undefined,
      validDays: isLeaf ? Math.max(1, Math.min(90, Number(d.validDays) || 1)) : 1,
    };
    if (out.nodeType === 'category') {
      out.noteText = '';
      out.noteImageUrl = '';
      out.description = '';
    } else if (out.nodeType === 'check') {
      out.description = '';
    }
    onSave(out);
  };

  const titleText = isExtraCreate ? '新增額外任務' : (isExtraContent ? '編輯內容' : (mode === 'edit' ? '編輯任務' : '新增任務'));

  return (
    <Modal title={titleText} onClose={onClose} wide>
      <div style={{ display: 'grid', gap: 12 }}>
        <div>
          <label style={S.label}>標題 *</label>
          <input style={{ ...S.input, width: '100%' }} value={d.title || ''} onChange={(e) => set('title', e.target.value)} />
        </div>
        <div>
          <label style={S.label}>類型</label>
          <select style={{ ...S.input, width: '100%' }} value={d.nodeType} onChange={(e) => set('nodeType', e.target.value)}
            disabled={isExtraContent || isExtraCreate}>
            {!isExtraCreate && !isExtraContent ? <option value="category">分類</option> : null}
            <option value="check">核選</option>
            <option value="text">填空</option>
          </select>
        </div>
        {!isExtraCreate && (
          <div>
            <label style={S.label}>父分類</label>
            <select style={{ ...S.input, width: '100%' }} value={d.parentId ?? ''} onChange={(e) => set('parentId', e.target.value ? Number(e.target.value) : null)}>
              <option value="">（根）</option>
              {cats.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
            </select>
          </div>
        )}

        {d.nodeType === 'text' && (
          <div>
            <label style={S.label}>填空提示</label>
            <input
              style={{ ...S.input, width: '100%' }}
              placeholder="前台輸入框提示文字"
              value={d.description || ''}
              onChange={(e) => set('description', e.target.value)}
            />
          </div>
        )}

        {isLeaf && (
          <>
            <div>
              <label style={S.label}>附註說明</label>
              <textarea
                style={{ ...S.input, width: '100%', minHeight: 72, resize: 'vertical' }}
                placeholder="可貼網址，員工可點開"
                value={d.noteText || ''}
                onChange={(e) => set('noteText', e.target.value)}
              />
            </div>
            <div>
              <label style={S.label}>附註圖片</label>
              <input ref={noteImgRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={onPickNoteImage} />
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <button type="button" style={S.btnGhost} disabled={imgUploading} onClick={() => noteImgRef.current?.click()}>
                  {imgUploading ? '上傳中…' : (d.noteImageUrl ? '更換圖片' : '上傳圖片')}
                </button>
                {d.noteImageUrl ? (
                  <button type="button" style={{ ...S.btnGhost, color: '#b91c1c' }} onClick={() => set('noteImageUrl', '')}>清除</button>
                ) : null}
              </div>
              {d.noteImageUrl ? (
                <img src={d.noteImageUrl} alt="" style={{ marginTop: 8, maxWidth: '100%', maxHeight: 160, borderRadius: 8, border: '1px solid #e5e7eb' }} />
              ) : null}
            </div>
          </>
        )}

        {isExtraCreate && (
          <div style={{ background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 10, padding: 12, display: 'grid', gap: 10 }}>
            <div style={{ fontWeight: 800, color: '#9a3412', fontSize: 13 }}>日期（當日全員可見）</div>
            <div>
              <label style={S.label}>日期</label>
              <input type="date" style={S.input} value={firstAssign.date} onChange={(e) => setFirstAssign((s) => ({ ...s, date: e.target.value }))} />
            </div>
          </div>
        )}

        {formScope === 'regular' && (
          <>
            <div>
              <label style={S.label}>週別（常態）</label>
              <WeekdayPicker value={d.scheduleDays || []} onChange={(v) => set('scheduleDays', v)} />
            </div>
            {isLeaf ? (
              <div>
                <label style={S.label}>完成頻率（有效天數）</label>
                <input
                  type="number"
                  min={1}
                  max={90}
                  style={{ ...S.input, width: 120 }}
                  value={d.validDays ?? 1}
                  onChange={(e) => set('validDays', Math.max(1, Math.min(90, Number(e.target.value) || 1)))}
                />
                <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>1＝每天（01:30 換日）；3＝勾選營業日起算 3 日內算完成</div>
              </div>
            ) : null}
            <div>
              <label style={S.label}>月頻（僅該周日顯示）</label>
              <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
                <label style={{ display: 'inline-flex', gap: 6, alignItems: 'center', fontSize: 13 }}>
                  <input type="checkbox" checked={!!d.monthFirstSunday} onChange={(e) => set('monthFirstSunday', e.target.checked)} />
                  每月第一周日（日號 1–7 的周日）
                </label>
                <label style={{ display: 'inline-flex', gap: 6, alignItems: 'center', fontSize: 13 }}>
                  <input type="checkbox" checked={!!d.monthLastSunday} onChange={(e) => set('monthLastSunday', e.target.checked)} />
                  每月最後周日（日號 25–31 的周日）
                </label>
              </div>
            </div>
            <div>
              <label style={S.label}>班別（空＝當日有開的班）</label>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                {SHIFT_OPTS.map((s) => (
                  <label key={s.id} style={{ display: 'inline-flex', gap: 4, alignItems: 'center', fontSize: 14 }}>
                    <input type="checkbox" checked={(d.shiftTypes || []).includes(s.id)} onChange={() => toggleShift(s.id)} />
                    {s.label}
                  </label>
                ))}
              </div>
            </div>
          </>
        )}

        <div>
          <label style={S.label}>排序</label>
          <input type="number" style={{ ...S.input, width: 120 }} value={d.sortOrder ?? 0} onChange={(e) => set('sortOrder', e.target.value)} />
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button style={S.btnGhost} onClick={onClose}>取消</button>
          <button style={S.btn()} onClick={handleSave}>儲存</button>
        </div>
      </div>
    </Modal>
  );
}


// ============================================================
// 近日公告
// ============================================================

async function uploadAnnImage(file) {
  const fd = new FormData();
  fd.append('file', file);
  const res = await fetch('/api/admin/shift/announcements/image', {
    method: 'POST',
    headers: { Authorization: `Bearer ${adminToken()}` },
    body: fd,
  });
  return res.json();
}

function RichTextEditor({ value, onChange }) {
  const ref = useRef(null);
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (ref.current && ref.current.innerHTML !== (value || '')) ref.current.innerHTML = value || '';
    // 僅在掛載時同步，避免打字時游標跳動
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const emit = () => onChange(ref.current?.innerHTML || '');
  const exec = (cmd, arg) => { document.execCommand(cmd, false, arg); ref.current?.focus(); emit(); };

  const onPickImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    try {
      const r = await uploadAnnImage(file);
      if (r?.ok && r.url) {
        ref.current?.focus();
        document.execCommand('insertHTML', false, `<img src="${r.url}" style="max-width:100%;border-radius:8px;margin:6px 0;" />`);
        emit();
      } else alert('圖片上傳失敗');
    } catch { alert('圖片上傳失敗'); }
    finally { setUploading(false); }
  };

  const tbtn = { padding: '4px 9px', border: '1px solid #d1d5db', borderRadius: 6, background: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 700 };

  return (
    <div style={{ border: '1px solid #d1d5db', borderRadius: 8, overflow: 'hidden' }}>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', padding: 8, borderBottom: '1px solid #e5e7eb', background: '#f9fafb', alignItems: 'center' }}>
        <button type="button" style={{ ...tbtn, fontWeight: 900 }} onClick={() => exec('bold')}>B</button>
        <button type="button" style={{ ...tbtn, fontStyle: 'italic' }} onClick={() => exec('italic')}>I</button>
        <button type="button" style={{ ...tbtn, textDecoration: 'underline' }} onClick={() => exec('underline')}>U</button>
        <span style={{ width: 1, height: 20, background: '#e5e7eb', margin: '0 2px' }} />
        {ANN_COLORS.map((c) => (
          <button key={c} type="button" title={c} onClick={() => exec('foreColor', c)}
            style={{ width: 22, height: 22, borderRadius: 5, border: '1px solid #d1d5db', background: c, cursor: 'pointer' }} />
        ))}
        <span style={{ width: 1, height: 20, background: '#e5e7eb', margin: '0 2px' }} />
        <button type="button" style={tbtn} onClick={() => exec('insertUnorderedList')}>• 清單</button>
        <button type="button" style={tbtn} onClick={() => fileRef.current?.click()} disabled={uploading}>{uploading ? '上傳中…' : '🖼 圖片'}</button>
        <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={onPickImage} />
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={emit}
        style={{ minHeight: 160, padding: 12, fontSize: 14, lineHeight: 1.6, outline: 'none' }}
      />
    </div>
  );
}

function AnnouncementTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [selectedId, setSelectedId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api('/api/admin/shift/announcements');
      if (r?.ok) {
        const list = r.announcements || [];
        setRows(list);
        setSelectedId((prev) => {
          if (prev && list.some((x) => x.id === prev)) return prev;
          return list[0]?.id ?? null;
        });
      }
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openEdit = (row) => {
    if (!row) return;
    setModal({
      mode: 'edit',
      data: {
        ...row,
        startDate: row.startDate ? String(row.startDate).slice(0, 10) : '',
        endDate: row.endDate ? String(row.endDate).slice(0, 10) : '',
        confirmStart: row.confirmStart ? String(row.confirmStart).slice(0, 10) : '',
        confirmEnd: row.confirmEnd ? String(row.confirmEnd).slice(0, 10) : '',
      },
    });
  };

  const save = async (data) => {
    const payload = {
      title: data.title,
      content: data.content || '',
      textColor: data.textColor || null,
      imageUrl: data.imageUrl || null,
      isActive: data.isActive !== false,
      startDate: data.startDate || null,
      endDate: data.endDate || null,
      confirmStart: data.confirmStart || null,
      confirmEnd: data.confirmEnd || null,
      sortOrder: Number(data.sortOrder) || 0,
    };
    let r;
    if (modal?.mode === 'edit') r = await api(`/api/admin/shift/announcements/${data.id}`, { method: 'PATCH', body: JSON.stringify(payload) });
    else r = await api('/api/admin/shift/announcements', { method: 'POST', body: JSON.stringify(payload) });
    if (r?.ok) { setModal(null); load(); } else alert('儲存失敗');
  };
  const toggleActive = async (row) => {
    const r = await api(`/api/admin/shift/announcements/${row.id}`, { method: 'PATCH', body: JSON.stringify({ isActive: !row.isActive }) });
    if (r?.ok) load();
  };
  const remove = async (id) => {
    if (!window.confirm('確定刪除此公告？')) return;
    const r = await api(`/api/admin/shift/announcements/${id}`, { method: 'DELETE' });
    if (r?.ok) load();
  };

  const selected = rows.find((r) => r.id === selectedId) || null;

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <button style={S.btn()} onClick={() => setModal({ mode: 'create', data: { title: '', content: '', textColor: '#b91c1c', imageUrl: '', isActive: true, startDate: '', endDate: '', confirmStart: '', confirmEnd: '', sortOrder: 0 } })}>＋ 新增公告</button>
      </div>
      {loading ? (
        <div style={S.card}><div style={{ padding: 20, color: '#888' }}>載入中…</div></div>
      ) : rows.length === 0 ? (
        <div style={S.card}><div style={{ padding: 20, color: '#9ca3af' }}>尚無公告</div></div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(280px, 1fr) minmax(300px, 420px)', gap: 16, alignItems: 'start' }}>
          <div style={{ ...S.card, padding: 12 }}>
            <div style={{ display: 'grid', gap: 10 }}>
              {rows.map((row) => {
                const on = row.id === selectedId;
                return (
                  <div
                    key={row.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => setSelectedId(row.id)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedId(row.id); } }}
                    style={{
                      border: on ? '2px solid #7b4f2f' : '1px solid #e5e7eb',
                      borderRadius: 10,
                      padding: 12,
                      opacity: row.isActive ? 1 : 0.65,
                      background: on ? '#faf6f2' : '#fff',
                      cursor: 'pointer',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 15, fontWeight: 800, color: row.textColor || '#111827' }}>{row.title}</div>
                        <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                          {row.isActive ? <Tag bg="#dcfce7" color="#166534">啟用中</Tag> : <Tag bg="#f3f4f6" color="#6b7280">已停用</Tag>}
                          <span style={{ marginLeft: 6 }}>顯示至 {row.endDate || '不限'}</span>
                          {(row.confirmStart || row.confirmEnd) ? (
                            <span style={{ marginLeft: 6 }}>確認 {row.confirmStart || '…'} ~ {row.confirmEnd || '…'}</span>
                          ) : null}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 4, flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
                        <button type="button" style={S.btnGhost} onClick={() => openEdit(row)}>編輯</button>
                        <button type="button" style={{ ...S.btnGhost, color: row.isActive ? '#b91c1c' : '#166534' }} onClick={() => toggleActive(row)}>{row.isActive ? '停用' : '啟用'}</button>
                        <button type="button" style={{ ...S.btnGhost, color: '#b91c1c' }} onClick={() => remove(row.id)}>刪除</button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div style={{ position: 'sticky', top: 12 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', marginBottom: 8 }}>
              前台預覽（點擊開啟編輯）
            </div>
            {selected ? (
              <button
                type="button"
                onClick={() => openEdit(selected)}
                title="點擊編輯"
                style={{
                  display: 'block', width: '100%', textAlign: 'left', cursor: 'pointer',
                  border: 'none', padding: 0, background: 'transparent',
                }}
              >
                <AnnouncementFrontPreview a={selected} />
              </button>
            ) : (
              <div style={{ ...S.card, padding: 24, color: '#9ca3af', textAlign: 'center' }}>請選擇左側公告</div>
            )}
          </div>
        </div>
      )}
      {modal && <AnnouncementModal mode={modal.mode} initial={modal.data} onClose={() => setModal(null)} onSave={save} />}
    </div>
  );
}

/** 後台用：模擬員工端公告卡 */
function AnnouncementFrontPreview({ a }) {
  return (
    <div style={{
      background: '#181c25', borderRadius: 14, padding: 16, color: '#e8eaf0',
      border: '1px solid #2e3548', boxShadow: '0 8px 24px rgba(0,0,0,.18)',
      minHeight: 200,
    }}>
      <div style={{ fontSize: 11, color: '#6b7494', marginBottom: 10, fontWeight: 700 }}>員工端預覽 · 點一下可編輯</div>
      <div style={{
        background: '#252b3a', borderRadius: 12, padding: 14,
        border: '1px solid #3a4158',
      }}>
        <div style={{ fontSize: 17, fontWeight: 800, color: a.textColor || '#e8eaf0', marginBottom: 10, lineHeight: 1.35 }}>
          {a.title || '（標題）'}
        </div>
        {a.imageUrl ? (
          <img src={a.imageUrl} alt="" style={{ width: '100%', maxHeight: 180, objectFit: 'cover', borderRadius: 8, marginBottom: 10, display: 'block' }} />
        ) : null}
        {a.content ? (
          <div
            style={{ fontSize: 14, lineHeight: 1.55, color: '#a8afc4' }}
            dangerouslySetInnerHTML={{ __html: sanitizeShiftHtml(a.content) }}
          />
        ) : (
          <div style={{ color: '#6b7494', fontSize: 13 }}>（尚無內容）</div>
        )}
        <div style={{ marginTop: 12, fontSize: 11, color: '#6b7494' }}>
          {a.confirmStart || a.confirmEnd ? `確認 ${a.confirmStart || '…'} ~ ${a.confirmEnd || '…'}` : null}
          {a.endDate ? `${a.confirmStart || a.confirmEnd ? ' · ' : ''}顯示至 ${a.endDate}` : ''}
        </div>
      </div>
    </div>
  );
}

function AnnouncementModal({ mode, initial, onClose, onSave }) {
  const [d, setD] = useState(() => ({
    ...initial,
    startDate: initial?.startDate ? String(initial.startDate).slice(0, 10) : '',
    endDate: initial?.endDate ? String(initial.endDate).slice(0, 10) : '',
    confirmStart: initial?.confirmStart ? String(initial.confirmStart).slice(0, 10) : '',
    confirmEnd: initial?.confirmEnd ? String(initial.confirmEnd).slice(0, 10) : '',
  }));
  const set = (k, v) => setD((s) => ({ ...s, [k]: v }));
  return (
    <Modal title={mode === 'edit' ? '編輯公告' : '新增公告'} onClose={onClose} wide>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, alignItems: 'start' }}>
        <div style={{ display: 'grid', gap: 12 }}>
          <div>
            <label style={S.label}>標題 *</label>
            <input style={{ ...S.input, width: '100%', color: d.textColor || '#111827', fontWeight: 800 }} value={d.title || ''} onChange={(e) => set('title', e.target.value)} />
          </div>
          <div>
            <label style={S.label}>標題顏色</label>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {ANN_COLORS.map((c) => (
                <button key={c} type="button" onClick={() => set('textColor', c)}
                  style={{ width: 26, height: 26, borderRadius: 6, cursor: 'pointer', background: c, border: d.textColor === c ? '3px solid #111' : '1px solid #d1d5db' }} />
              ))}
            </div>
          </div>
          <div>
            <label style={S.label}>內容</label>
            <RichTextEditor value={d.content || ''} onChange={(v) => set('content', v)} />
          </div>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <div>
              <label style={S.label}>顯示起始</label>
              <input type="date" style={S.input} value={d.startDate || ''} onChange={(e) => set('startDate', e.target.value)} />
            </div>
            <div>
              <label style={S.label}>顯示截止</label>
              <input type="date" style={S.input} value={d.endDate || ''} onChange={(e) => set('endDate', e.target.value)} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <div>
              <label style={S.label}>每日確認起</label>
              <input type="date" style={S.input} value={d.confirmStart || ''} onChange={(e) => set('confirmStart', e.target.value)} />
            </div>
            <div>
              <label style={S.label}>每日確認迄</label>
              <input type="date" style={S.input} value={d.confirmEnd || ''} onChange={(e) => set('confirmEnd', e.target.value)} />
            </div>
            <div>
              <label style={S.label}>排序</label>
              <input type="number" style={{ ...S.input, width: 100 }} value={d.sortOrder ?? 0} onChange={(e) => set('sortOrder', e.target.value)} />
            </div>
          </div>
          <label style={{ display: 'inline-flex', gap: 6, alignItems: 'center', fontSize: 14 }}>
            <input type="checkbox" checked={d.isActive !== false} onChange={(e) => set('isActive', e.target.checked)} />
            立即啟用
          </label>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button style={S.btnGhost} onClick={onClose}>取消</button>
            <button style={S.btn()} onClick={() => { if (!(d.title || '').trim()) { alert('請填標題'); return; } onSave(d); }}>儲存</button>
          </div>
        </div>
        <div>
          <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 8, fontWeight: 700 }}>即時預覽</div>
          <AnnouncementFrontPreview a={d} />
        </div>
      </div>
    </Modal>
  );
}

function mondayOf(ymd) {
  const m = String(ymd || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return todayYmd();
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const wd = d.getDay();
  d.setDate(d.getDate() + (wd === 0 ? -6 : 1 - wd));
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function weekDaysOf(monday) {
  return [0, 1, 2, 3, 4, 5, 6].map((i) => addDaysYmd(monday, i));
}

const WEEK_COL = ['一', '二', '三', '四', '五', '六', '日'];

function buildAdminTree(flat) {
  const byParent = new Map();
  for (const n of flat || []) {
    const pid = n.parentId == null ? 'root' : n.parentId;
    if (!byParent.has(pid)) byParent.set(pid, []);
    byParent.get(pid).push(n);
  }
  const walk = (pid) => (byParent.get(pid) || [])
    .slice()
    .sort((a, b) => (a.sortOrder - b.sortOrder) || (a.id - b.id))
    .map((n) => ({ ...n, children: walk(n.id) }));
  return walk('root');
}

// ============================================================
// 追蹤管理：週檢視 / 檢視表 / 日快覽 / 單題歷史
// ============================================================
const KIND_LABEL = { regular: '常態', shiftExtra: '班別額外', personalExtra: '個人額外' };
const KIND_COLOR = {
  regular: { bg: '#e0f2fe', color: '#0369a1' },
  shiftExtra: { bg: '#ffedd5', color: '#c2410c' },
  personalExtra: { bg: '#f3e8ff', color: '#7c3aed' },
};

function addDaysYmd(ymd, delta) {
  const m = String(ymd || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return todayYmd();
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  d.setDate(d.getDate() + delta);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function WeekGrid({
  weekStart, setWeekStart, weekShift, setWeekShift, weekKind, setWeekKind,
  treeNodes, weekRows, loading, collapsed, setCollapsed,
  selCats, setSelCats, selLeaves, setSelLeaves, onQuery, onOpenHistory,
}) {
  const days = weekDaysOf(weekStart);
  const tree = useMemo(() => buildAdminTree(treeNodes), [treeNodes]);
  const cellMap = useMemo(() => {
    const m = new Map();
    for (const r of weekRows || []) {
      m.set(`${r.nodeId}:${r.bizDate}`, r);
    }
    return m;
  }, [weekRows]);

  const catFilterOn = selCats.size > 0;
  const leafFilterOn = selLeaves.size > 0;

  const toggleId = (setFn, set, id) => {
    const next = new Set(set);
    if (next.has(id)) next.delete(id); else next.add(id);
    setFn(next);
  };

  const filterTree = (nodes) => (nodes || []).map((n) => {
    const children = filterTree(n.children || []);
    if (n.nodeType === 'category') {
      const selfSel = !catFilterOn || selCats.has(n.id);
      if (!selfSel && children.length === 0) return null;
      if (leafFilterOn && children.length === 0) return null;
      return { ...n, children };
    }
    if (leafFilterOn && !selLeaves.has(n.id)) return null;
    return { ...n, children };
  }).filter(Boolean);

  const shown = filterTree(tree);

  const renderCell = (leaf, day) => {
    const r = cellMap.get(`${leaf.id}:${day}`);
    if (!r) return <span style={{ color: '#d1d5db' }}>—</span>;
    const done = r.status === 'done';
    const text = r.nodeType === 'text' && r.textValue ? String(r.textValue).slice(0, 18) : '';
    return (
      <button
        type="button"
        title={text || (done ? '已做' : '未做')}
        onClick={() => onOpenHistory(leaf.id)}
        style={{
          border: 'none', background: 'transparent', cursor: 'pointer',
          fontSize: 12, fontWeight: 700, width: '100%', textAlign: 'left', padding: 0,
          color: done ? '#047857' : '#b91c1c',
        }}
      >
        {done ? (r.doneSource === 'inherited' ? '✓繼承' : '✓') : '☐'}
        {text ? <div style={{ fontWeight: 500, color: '#6b7280', fontSize: 11 }}>{text}</div> : null}
      </button>
    );
  };

  const renderRows = (nodes, depth = 0) => {
    const out = [];
    for (const n of nodes || []) {
      const isCat = n.nodeType === 'category';
      const key = String(n.id);
      const isOpen = collapsed[key] !== true;
      if (isCat) {
        out.push(
          <tr key={`c-${n.id}`}>
            <td style={{ ...S.td, paddingLeft: 8 + depth * 14, whiteSpace: 'nowrap', background: '#f8fafc', fontWeight: 800 }} colSpan={1}>
              <button type="button" style={{ ...S.btnGhost, padding: '2px 8px', marginRight: 6 }} onClick={() => setCollapsed((c) => ({ ...c, [key]: isOpen }))}>
                {isOpen ? '▾' : '▸'}
              </button>
              <label style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                <input type="checkbox" checked={selCats.has(n.id)} onChange={() => toggleId(setSelCats, selCats, n.id)} />
                {n.title}
              </label>
            </td>
            {days.map((d) => <td key={d} style={{ ...S.td, background: '#f8fafc' }} />)}
          </tr>
        );
        if (isOpen) out.push(...renderRows(n.children || [], depth + 1));
      } else {
        out.push(
          <tr key={`l-${n.id}`}>
            <td style={{ ...S.td, paddingLeft: 8 + depth * 14 }}>
              <label style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                <input type="checkbox" checked={selLeaves.has(n.id)} onChange={() => toggleId(setSelLeaves, selLeaves, n.id)} />
                {n.title}
              </label>
            </td>
            {days.map((d) => (
              <td key={d} style={{ ...S.td, minWidth: 72, verticalAlign: 'top' }}>{renderCell(n, d)}</td>
            ))}
          </tr>
        );
      }
    }
    return out;
  };

  return (
    <>
      <div style={{ ...S.card, display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
        <button type="button" style={S.btnGhost} onClick={() => setWeekStart(addDaysYmd(weekStart, -7))}>上一週</button>
        <input
          type="date"
          value={weekStart}
          onChange={(e) => setWeekStart(mondayOf(e.target.value))}
          style={S.input}
        />
        <button type="button" style={S.btnGhost} onClick={() => setWeekStart(addDaysYmd(weekStart, 7))}>下一週</button>
        <button type="button" style={S.btnGhost} onClick={() => setWeekStart(mondayOf(todayYmd()))}>本週</button>
        <select value={weekShift} onChange={(e) => setWeekShift(e.target.value)} style={S.input}>
          <option value="">班別（合併）</option>
          <option value="early">早班</option>
          <option value="late">晚班</option>
          <option value="night">夜班</option>
          <option value="maintenance">維護班</option>
        </select>
        <select value={weekKind} onChange={(e) => setWeekKind(e.target.value)} style={S.input}>
          <option value="">類型：全部</option>
          <option value="regular">常態</option>
          <option value="shiftExtra">班別額外</option>
          <option value="personalExtra">個人額外</option>
        </select>
        <button type="button" style={S.btn()} onClick={onQuery}>查詢</button>
        <button type="button" style={S.btnGhost} onClick={() => { setSelCats(new Set()); setSelLeaves(new Set()); }}>清除勾選</button>
        <span style={{ fontSize: 12, color: '#6b7280' }}>勾選分類／任務以篩選；未勾＝全部</span>
      </div>
      <div style={{ ...S.card, overflowX: 'auto' }}>
        {loading ? <div style={{ padding: 20, color: '#888' }}>載入中…</div> : (
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 860 }}>
            <thead>
              <tr>
                <th style={{ ...S.th, minWidth: 220 }}>項目</th>
                {days.map((d, i) => (
                  <th key={d} style={S.th}>{WEEK_COL[i]}<div style={{ fontWeight: 500, color: '#9ca3af' }}>{d.slice(5)}</div></th>
                ))}
              </tr>
            </thead>
            <tbody>
              {shown.length === 0 ? (
                <tr><td style={S.td} colSpan={8}>無資料</td></tr>
              ) : renderRows(shown)}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}

function TrackingTab() {
  const [sub, setSub] = useState('week'); // week | sheet | day | history
  const [rangeMode, setRangeMode] = useState(false);
  const [date, setDate] = useState(todayYmd);
  const [from, setFrom] = useState(() => addDaysYmd(todayYmd(), -6));
  const [to, setTo] = useState(todayYmd);
  const [shiftType, setShiftType] = useState('');
  const [kind, setKind] = useState('');
  const [status, setStatus] = useState('');
  const [staffRole, setStaffRole] = useState('doer');
  const [staffCode, setStaffCode] = useState('');
  const [onlyMultiDay, setOnlyMultiDay] = useState(false);
  const [q, setQ] = useState('');
  const [staffList, setStaffList] = useState([]);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({ total: 0, done: 0, pending: 0 });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  // 日快覽
  const [dayBiz, setDayBiz] = useState(todayYmd);
  const [dayData, setDayData] = useState(null);
  const [dayOpen, setDayOpen] = useState({ early: true });

  // 單題歷史
  const [histNodeId, setHistNodeId] = useState('');
  const [histFrom, setHistFrom] = useState(() => addDaysYmd(todayYmd(), -29));
  const [histTo, setHistTo] = useState(todayYmd);
  const [histData, setHistData] = useState(null);
  const [leafOptions, setLeafOptions] = useState([]);
  const [treeNodes, setTreeNodes] = useState([]);
  const [weekStart, setWeekStart] = useState(() => mondayOf(todayYmd()));
  const [weekRows, setWeekRows] = useState([]);
  const [weekCollapsed, setWeekCollapsed] = useState({});
  const [selCats, setSelCats] = useState(() => new Set());
  const [selLeaves, setSelLeaves] = useState(() => new Set());
  const [weekKind, setWeekKind] = useState('');
  const [weekShift, setWeekShift] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const st = await api('/api/admin/shift/staff');
        if (st?.ok) setStaffList((st.staff || []).filter((s) => s.isActive && s.staffCode));
      } catch { /* ignore */ }
      try {
        const tr = await api('/api/admin/shift/tree');
        if (tr?.ok) {
          const flat = tr.nodes || [];
          const byId = new Map(flat.map((n) => [n.id, n]));
          const pathOf = (id) => {
            const parts = [];
            let cur = byId.get(id);
            const guard = new Set();
            while (cur?.parentId != null && !guard.has(cur.parentId)) {
              guard.add(cur.parentId);
              const p = byId.get(cur.parentId);
              if (!p) break;
              if (p.nodeType === 'category') parts.unshift(p.title);
              cur = p;
            }
            return parts.join(' / ');
          };
          setTreeNodes(flat.filter((n) => n.isActive !== false));
          const leaves = flat
            .filter((n) => (n.nodeType === 'check' || n.nodeType === 'text') && n.isActive !== false)
            .map((n) => ({
              id: n.id,
              title: n.title,
              path: pathOf(n.id),
              validDays: n.validDays || 1,
            }));
          setLeafOptions(leaves);
        }
      } catch { /* ignore */ }
    })();
  }, []);

  const loadSheet = useCallback(async (overrides = {}) => {
    setLoading(true); setErr('');
    try {
      const useRange = overrides.rangeMode != null ? overrides.rangeMode : rangeMode;
      const useDate = overrides.date || date;
      const useFrom = overrides.from || from;
      const useTo = overrides.to || to;
      const params = new URLSearchParams();
      if (useRange) {
        params.set('from', useFrom);
        params.set('to', useTo);
      } else {
        params.set('date', useDate);
      }
      if (shiftType) params.set('shiftType', shiftType);
      if (kind) params.set('kind', kind);
      if (status) params.set('status', status);
      if (staffCode) {
        params.set('staffCode', staffCode);
        params.set('staffRole', staffRole);
      }
      if (onlyMultiDay) params.set('onlyMultiDay', '1');
      if (q.trim()) params.set('q', q.trim());
      const d = await api(`/api/admin/shift/tracking/sheet?${params}`);
      if (!d?.ok) throw new Error(d?.message || d?.error || '載入失敗');
      setRows(d.rows || []);
      setSummary(d.summary || { total: 0, done: 0, pending: 0 });
    } catch (e) {
      setErr(e.message || '載入失敗');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [rangeMode, from, to, date, shiftType, kind, status, staffCode, staffRole, onlyMultiDay, q]);

  useEffect(() => {
    if (sub === 'sheet') loadSheet();
    // 僅在進入檢視表時自動查；篩選變更請按「查詢」
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sub]);

  const loadWeek = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const fromW = weekStart;
      const toW = addDaysYmd(weekStart, 6);
      const params = new URLSearchParams();
      params.set('from', fromW);
      params.set('to', toW);
      if (weekShift) params.set('shiftType', weekShift);
      if (weekKind) params.set('kind', weekKind);
      const d = await api(`/api/admin/shift/tracking/sheet?${params}`);
      if (!d?.ok) throw new Error(d?.message || d?.error || '載入失敗');
      setWeekRows(d.rows || []);
    } catch (e) {
      setErr(e.message || '載入失敗');
      setWeekRows([]);
    } finally {
      setLoading(false);
    }
  }, [weekStart, weekShift, weekKind]);

  useEffect(() => {
    if (sub === 'week') loadWeek();
  }, [sub, loadWeek]);

  const loadDay = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const d = await api(`/api/admin/shift/overview?date=${dayBiz}`);
      if (!d?.ok) throw new Error(d?.message || '載入失敗');
      setDayData(d);
    } catch (e) {
      setErr(e.message || '載入失敗');
      setDayData(null);
    } finally {
      setLoading(false);
    }
  }, [dayBiz]);

  useEffect(() => {
    if (sub === 'day') loadDay();
  }, [sub, loadDay]);

  const loadHistory = useCallback(async (nid) => {
    const id = Number(nid || histNodeId);
    if (!Number.isFinite(id) || !id) {
      setHistData(null);
      return;
    }
    setLoading(true); setErr('');
    try {
      const d = await api(`/api/admin/shift/tracking/node/${id}/history?from=${histFrom}&to=${histTo}`);
      if (!d?.ok) throw new Error(d?.message || '載入失敗');
      setHistData(d);
    } catch (e) {
      setErr(e.message || '載入失敗');
      setHistData(null);
    } finally {
      setLoading(false);
    }
  }, [histNodeId, histFrom, histTo]);

  const clearValidity = async (nodeId, sourceBizDate) => {
    if (!sourceBizDate) return;
    if (!window.confirm(`清除 ${sourceBizDate} 的完成有效？之後區間內將視為未做。`)) return;
    const r = await api(`/api/admin/shift/tracking/node/${nodeId}/clear-validity`, {
      method: 'POST',
      body: JSON.stringify({ sourceBizDate }),
    });
    if (r?.ok) {
      if (sub === 'sheet') loadSheet();
      if (sub === 'history') loadHistory(nodeId);
      if (sub === 'day') loadDay();
    } else alert(r?.message || '清除失敗');
  };

  const openHistory = (nodeId) => {
    setHistNodeId(String(nodeId));
    setSub('history');
    setTimeout(() => loadHistory(nodeId), 0);
  };

  const subBtn = (key, label) => (
    <button
      type="button"
      key={key}
      onClick={() => setSub(key)}
      style={{
        ...S.btnGhost,
        background: sub === key ? '#7b4f2f' : '#fff',
        color: sub === key ? '#fff' : '#374151',
        borderColor: sub === key ? '#5e3a1a' : '#d1d5db',
      }}
    >
      {label}
    </button>
  );

  const renderDayLeaves = (nodes, depth = 0) => {
    const out = [];
    const walk = (arr, d) => {
      for (const n of arr || []) {
        if (n.nodeType === 'check' || n.nodeType === 'text') {
          const vd = Number(n.validDays) || 1;
          out.push(
            <div key={`${n.id}-${d}`} style={{
              padding: '8px 10px', marginLeft: d * 12, marginBottom: 4,
              background: n.done ? '#ecfdf5' : '#fff7ed',
              border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 13,
            }}>
              <div style={{ fontWeight: 700 }}>
                {n.done ? '✓ ' : '☐ '}{n.title}
                {n.done ? <Tag bg="#d1fae5" color="#047857">已做 {n.sourceBizDate || ''}</Tag> : null}
              </div>
              {vd > 1 && n.validUntil ? <div style={{ fontSize: 12, color: '#b45309' }}>有效至 {n.validUntil}</div> : null}
              {n.record?.doneByName ? <div style={{ fontSize: 12, color: '#6b7280' }}>{n.record.doneByName}</div> : null}
              {n.nodeType === 'text' && n.record?.textValue ? (
                <div style={{ marginTop: 4, whiteSpace: 'pre-wrap' }}>{n.record.textValue}</div>
              ) : null}
              {n.done && n.sourceBizDate ? (
                <button type="button" style={{ ...S.btnGhost, marginTop: 6, fontSize: 12 }} onClick={() => clearValidity(n.id, n.sourceBizDate)}>清除有效</button>
              ) : null}
              <button type="button" style={{ ...S.btnGhost, marginTop: 6, marginLeft: 6, fontSize: 12 }} onClick={() => openHistory(n.id)}>歷史</button>
            </div>
          );
        }
        if (n.children?.length) walk(n.children, d + 1);
      }
    };
    walk(nodes, depth);
    return out;
  };

  return (
    <div>
      <div style={{ ...S.card, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <h3 style={{ ...S.h, margin: 0, marginRight: 8 }}>追蹤管理</h3>
        {subBtn('week', '週檢視')}
        {subBtn('sheet', '檢視表')}
        {subBtn('day', '日快覽')}
        {subBtn('history', '單題歷史')}
      </div>

      {err ? <div style={{ ...S.card, color: '#b91c1c' }}>{err}</div> : null}

      {sub === 'week' && (
        <WeekGrid
          weekStart={weekStart}
          setWeekStart={setWeekStart}
          weekShift={weekShift}
          setWeekShift={setWeekShift}
          weekKind={weekKind}
          setWeekKind={setWeekKind}
          treeNodes={treeNodes}
          weekRows={weekRows}
          loading={loading}
          collapsed={weekCollapsed}
          setCollapsed={setWeekCollapsed}
          selCats={selCats}
          setSelCats={setSelCats}
          selLeaves={selLeaves}
          setSelLeaves={setSelLeaves}
          onQuery={loadWeek}
          onOpenHistory={openHistory}
        />
      )}

      {sub === 'sheet' && (
        <>
          <div style={{ ...S.card, display: 'grid', gap: 10 }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
              <label style={{ display: 'inline-flex', gap: 6, alignItems: 'center', fontSize: 13 }}>
                <input type="checkbox" checked={rangeMode} onChange={(e) => setRangeMode(e.target.checked)} />
                區間
              </label>
              {rangeMode ? (
                <>
                  <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} style={S.input} />
                  <span>～</span>
                  <input type="date" value={to} onChange={(e) => setTo(e.target.value)} style={S.input} />
                </>
              ) : (
                <input type="date" value={date} onChange={(e) => setDate(e.target.value)} style={S.input} />
              )}
              <select value={shiftType} onChange={(e) => setShiftType(e.target.value)} style={S.input}>
                <option value="">班別（合併）</option>
                <option value="early">早班</option>
                <option value="late">晚班</option>
                <option value="night">夜班</option>
                <option value="maintenance">維護班</option>
              </select>
              <select value={kind} onChange={(e) => setKind(e.target.value)} style={S.input}>
                <option value="">類型：全部</option>
                <option value="regular">常態</option>
                <option value="shiftExtra">班別額外</option>
                <option value="personalExtra">個人額外</option>
              </select>
              <select value={status} onChange={(e) => setStatus(e.target.value)} style={S.input}>
                <option value="">狀態：全部</option>
                <option value="pending">未做</option>
                <option value="done">已做</option>
              </select>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
              <select value={staffRole} onChange={(e) => setStaffRole(e.target.value)} style={S.input}>
                <option value="doer">執行人</option>
                <option value="assignee">被指派</option>
              </select>
              <select value={staffCode} onChange={(e) => setStaffCode(e.target.value)} style={{ ...S.input, minWidth: 140 }}>
                <option value="">人員：全部</option>
                {staffList.map((s) => (
                  <option key={s.staffCode} value={s.staffCode}>{s.name}（{s.staffCode}）</option>
                ))}
              </select>
              <label style={{ display: 'inline-flex', gap: 6, alignItems: 'center', fontSize: 13 }}>
                <input type="checkbox" checked={onlyMultiDay} onChange={(e) => setOnlyMultiDay(e.target.checked)} />
                只看非每日
              </label>
              <input
                style={{ ...S.input, minWidth: 160 }}
                placeholder="關鍵字（標題／路徑）"
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
              <button type="button" style={S.btn()} onClick={loadSheet}>查詢</button>
              <span style={{ fontSize: 13, color: '#6b7280' }}>
                共 {summary.total} · 已做 {summary.done} · 未做 {summary.pending}
              </span>
            </div>
          </div>

          <div style={{ ...S.card, overflowX: 'auto' }}>
            {loading ? <div style={{ padding: 20, color: '#888' }}>載入中…</div> : (
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 900 }}>
                <thead>
                  <tr>
                    {rangeMode ? <th style={S.th}>日期</th> : null}
                    <th style={S.th}>類型</th>
                    <th style={S.th}>任務</th>
                    <th style={S.th}>指派／班</th>
                    <th style={S.th}>頻率</th>
                    <th style={S.th}>狀態</th>
                    <th style={S.th}>完成日</th>
                    <th style={S.th}>有效至</th>
                    <th style={S.th}>執行人</th>
                    <th style={S.th}>填空</th>
                    <th style={S.th}>操作</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 && (
                    <tr><td style={S.td} colSpan={rangeMode ? 11 : 10}>無資料</td></tr>
                  )}
                  {rows.map((r) => {
                    const kc = KIND_COLOR[r.kind] || KIND_COLOR.regular;
                    const pending = r.status !== 'done';
                    return (
                      <tr key={`${r.bizDate}-${r.nodeId}-${r.shiftType}`} style={{ background: pending ? '#fffbeb' : undefined }}>
                        {rangeMode ? <td style={S.td}>{r.bizDate}</td> : null}
                        <td style={S.td}><Tag bg={kc.bg} color={kc.color}>{KIND_LABEL[r.kind] || r.kind}</Tag></td>
                        <td style={S.td}>
                          <div style={{ fontWeight: 700 }}>{r.title}</div>
                          {r.path ? <div style={{ fontSize: 12, color: '#9ca3af' }}>{r.path}</div> : null}
                        </td>
                        <td style={S.td}>
                          {r.assigneeStaffName || (r.assigneeShiftType ? (SHIFT_LABEL[r.assigneeShiftType] || r.assigneeShiftType) : (SHIFT_LABEL[r.shiftType] || r.shiftType || '—'))}
                        </td>
                        <td style={S.td}>{(r.validDays || 1) > 1 ? `每${r.validDays}日` : '每天'}</td>
                        <td style={S.td}>
                          {r.status === 'done'
                            ? <Tag bg="#d1fae5" color="#047857">{r.doneSource === 'inherited' ? '已做・繼承' : '已做'}</Tag>
                            : <Tag bg="#fee2e2" color="#b91c1c">未做</Tag>}
                        </td>
                        <td style={S.td}>{r.status === 'done' ? (r.sourceBizDate || '—') : '—'}</td>
                        <td style={S.td}>{(r.validDays || 1) > 1 ? (r.validUntil || '—') : '—'}</td>
                        <td style={S.td}>{r.doneByName || '—'}</td>
                        <td style={S.td}>
                          {r.nodeType === 'text' && r.textValue
                            ? <span title={r.textValue}>{String(r.textValue).slice(0, 40)}{String(r.textValue).length > 40 ? '…' : ''}</span>
                            : '—'}
                        </td>
                        <td style={S.td}>
                          <button type="button" style={S.btnGhost} onClick={() => openHistory(r.nodeId)}>歷史</button>
                          {r.status === 'done' && r.sourceBizDate ? (
                            <button type="button" style={{ ...S.btnGhost, marginLeft: 4, color: '#b91c1c' }} onClick={() => clearValidity(r.nodeId, r.sourceBizDate)}>清除有效</button>
                          ) : null}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      {sub === 'day' && (
        <>
          <div style={{ ...S.card, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
            <input type="date" value={dayBiz} onChange={(e) => setDayBiz(e.target.value)} style={S.input} />
            <button type="button" style={S.btnGhost} onClick={loadDay}>重新整理</button>
            <button
              type="button"
              style={S.btn('#0369a1')}
              onClick={() => {
                setRangeMode(false);
                setDate(dayBiz);
                setSub('sheet');
                loadSheet({ rangeMode: false, date: dayBiz });
              }}
            >
              在檢視表看這天
            </button>
          </div>
          {loading ? <div style={{ padding: 20, color: '#888' }}>載入中…</div> : null}
          {dayData && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 12 }}>
              {['early', 'late', 'night', 'maintenance'].map((st) => {
                const block = dayData.byShift?.[st];
                if (!block) return null;
                const p = block.progress || {};
                const isOpen = !!dayOpen[st];
                return (
                  <div key={st} style={{ ...S.card, margin: 0 }}>
                    <button
                      type="button"
                      style={{ width: '100%', border: 'none', background: 'transparent', textAlign: 'left', cursor: 'pointer', padding: 0 }}
                      onClick={() => setDayOpen((o) => ({ ...o, [st]: !o[st] }))}
                    >
                      <strong>{SHIFT_LABEL[st]} {isOpen ? '▾' : '▸'}</strong>
                      <span style={{ float: 'right', fontWeight: 800 }}>{p.pct || 0}%</span>
                      <div style={{ fontSize: 12, color: '#6b7280' }}>{p.done}/{p.total} 完成</div>
                    </button>
                    {isOpen && (
                      <div style={{ marginTop: 10, maxHeight: 420, overflow: 'auto' }}>
                        {renderDayLeaves(block.shiftExtra)}
                        {renderDayLeaves(block.regular)}
                        {renderDayLeaves(block.personalExtra)}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {sub === 'history' && (
        <>
          <div style={{ ...S.card, display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
            <select
              style={{ ...S.input, minWidth: 260 }}
              value={histNodeId}
              onChange={(e) => {
                setHistNodeId(e.target.value);
                loadHistory(e.target.value);
              }}
            >
              <option value="">選擇任務…</option>
              {leafOptions.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.path ? `${l.path} / ` : ''}{l.title}{(l.validDays || 1) > 1 ? `（每${l.validDays}日）` : ''}
                </option>
              ))}
            </select>
            <input type="date" value={histFrom} onChange={(e) => setHistFrom(e.target.value)} style={S.input} />
            <span>～</span>
            <input type="date" value={histTo} onChange={(e) => setHistTo(e.target.value)} style={S.input} />
            <button type="button" style={S.btn()} onClick={() => loadHistory()}>查詢</button>
          </div>
          {histData?.node && (
            <div style={S.card}>
              <div style={{ fontWeight: 800, marginBottom: 8 }}>
                {histData.node.path ? `${histData.node.path} / ` : ''}{histData.node.title}
                {(histData.node.validDays || 1) > 1 ? <Tag bg="#ffedd5" color="#c2410c">每{histData.node.validDays}日</Tag> : null}
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 700 }}>
                  <thead>
                    <tr>
                      <th style={S.th}>營業日</th>
                      <th style={S.th}>可見</th>
                      <th style={S.th}>狀態</th>
                      <th style={S.th}>完成日</th>
                      <th style={S.th}>有效至</th>
                      <th style={S.th}>執行人</th>
                      <th style={S.th}>內容</th>
                      <th style={S.th}>操作</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(histData.timeline || []).map((t) => (
                      <tr key={t.bizDate} style={{ background: t.status === 'pending' && t.visible ? '#fffbeb' : undefined }}>
                        <td style={S.td}>{t.bizDate}</td>
                        <td style={S.td}>{t.visible ? '是' : '—'}</td>
                        <td style={S.td}>
                          {t.status === 'done'
                            ? <Tag bg="#d1fae5" color="#047857">{t.doneSource === 'inherited' ? '已做・繼承' : '已做'}</Tag>
                            : (t.visible ? <Tag bg="#fee2e2" color="#b91c1c">未做</Tag> : '—')}
                        </td>
                        <td style={S.td}>{t.sourceBizDate || '—'}</td>
                        <td style={S.td}>{t.validUntil || '—'}</td>
                        <td style={S.td}>{t.doneByName || '—'}</td>
                        <td style={S.td}>{t.textValue ? String(t.textValue).slice(0, 60) : '—'}</td>
                        <td style={S.td}>
                          {t.status === 'done' && t.sourceBizDate === t.bizDate ? (
                            <button type="button" style={{ ...S.btnGhost, color: '#b91c1c' }} onClick={() => clearValidity(histData.node.id, t.sourceBizDate)}>清除有效</button>
                          ) : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          {loading ? <div style={{ padding: 20, color: '#888' }}>載入中…</div> : null}
        </>
      )}
    </div>
  );
}

// ============================================================
// 主元件
// ============================================================
const TABS_OPS = [
  { key: 'dashboard', label: '今日快覽', C: DashboardTab },
  { key: 'month', label: '月排班', C: ScheduleMonthAdmin },
  { key: 'tree', label: '任務管理', C: TreeTab },
  { key: 'tracking', label: '追蹤管理', C: TrackingTab },
];
const TABS_SETTINGS = [
  { key: 'staff', label: '員工管理', C: StaffTab },
  { key: 'announcement', label: '近日公告', C: AnnouncementTab },
  { key: 'anomalies', label: '異常回報', C: AnomaliesTab },
];
const TABS = [...TABS_OPS, ...TABS_SETTINGS];

export default function ShiftAdminHub() {
  const [tab, setTab] = useState('dashboard');
  const Active = TABS.find(t => t.key === tab)?.C || DashboardTab;

  const subTabStyle = (active) => ({
    padding: '7px 14px', borderRadius: 8, border: active ? '1px solid #5e3a1a' : '1px solid #d1d5db',
    background: active ? '#7b4f2f' : '#fff', color: active ? '#fff' : '#374151',
    fontSize: 14, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap',
  });

  return (
    <div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 16, alignItems: 'center' }}>
        {TABS_OPS.map(t => (
          <button key={t.key} style={subTabStyle(tab === t.key)} onClick={() => setTab(t.key)}>{t.label}</button>
        ))}
        <span style={{ width: 1, height: 24, background: '#d1d5db', margin: '0 4px' }} />
        {TABS_SETTINGS.map(t => (
          <button key={t.key} style={subTabStyle(tab === t.key)} onClick={() => setTab(t.key)}>{t.label}</button>
        ))}
      </div>
      <Active />
    </div>
  );
}
